import React, { useState, useEffect, useRef } from 'react'
import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { Tombola as TombolaOverlay } from './../../Overlays/Overlays/Tombola'
import { Contacto as ContactoOverlay } from './../../Overlays/Overlays/Contacto'
import { Portada as PortadaOverlay } from './../../Overlays/Overlays/Portada'
import { Soporte as SoporteOverlay } from './../../Overlays/Overlays/Soporte'
import { Cumpleanos as CumpleanosOverlay } from './../../Overlays/Overlays/Cumpleanos'
import LogoOverlay from './../../Overlays/Overlays/LogoOverlay'
import { Confirm } from './../../Commons/Modals/Confirm'
import {
    GetTombolaConfig,
    SetTombolaConfig,
    ShowTombolaOverlay,
    HideTombolaOverlay,
    GetContactoConfig,
    SetContactoConfig,
    ShowContactoOverlay,
    HideContactoOverlay,
    CreateContactoItem,
    UpdateContactoItem,
    DeleteContactoItem,
    SelectContactoItem,
    ToggleContactoVisibility,
    GetPortadaConfig,
    SetPortadaConfig,
    ShowPortadaOverlay,
    HidePortadaOverlay,
    GetSoporteConfig,
    SetSoporteConfig,
    ShowSoporteOverlay,
    HideSoporteOverlay,
    GetCumpleanosConfig,
    SetCumpleanosConfig,
    ShowCumpleanosOverlay,
    HideCumpleanosOverlay,
    CreateLiveBadgeItem,
    UpdateLiveBadgeItem,
    DeleteLiveBadgeItem,
    SelectLiveBadgeItem,
    ToggleLiveBadgeLogo
} from './../../../utilities/BL/Tombola'
import {
    GetLogoConfig,
    SetLogoConfig,
    ToggleLogoVisibility
} from './../../../utilities/BL/Logo'
import {
    GetMencionesConfig,
    SetMencionesConfig,
    ToggleMencionesVisibility,
    CreateMencion,
    UpdateMencion,
    DeleteMencion,
    SelectMencion
} from './../../../utilities/DL/Menciones'
import {
    GetPreguntasConfig,
    SetPreguntasConfig,
    TogglePreguntasVisibility,
    CreatePregunta,
    UpdatePregunta,
    DeletePregunta,
    SelectPregunta
} from './../../../utilities/DL/Preguntas'
import {
    GetPremiosConfig,
    SetPremiosConfig,
    TogglePremiosVisibility,
    CreatePremio,
    UpdatePremio,
    DeletePremio,
    SelectPremio
} from './../../../utilities/DL/Premios'

/**
 * COMPONENTE: PANEL DE CONTROL DE TOMBOLA Y CONTACTO (ADMIN)
 * ------------------------------------------------------------------
 * Panel unificado del operador para controlar la insignia "En Vivo",
 * la placa de información "Contacto (WhatsApp)" y la "Portada (Cover)".
 * Ambos elementos se estructuran en pestañas plegables (Cards Accordion).
 * ------------------------------------------------------------------
 */
const Tombola = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();
    const [isLoading, setIsLoading] = useState(false);

    // Helper reusable para renderizar sliders combinados con input manual de valor
    const renderSliderWithInput = (label, value, min, max, step, suffix, onChange) => {
        return (
            <div className="tombola-slider-group mb-3">
                <div className="slider-header d-flex justify-content-between align-items-center text-xs mb-1">
                    <span className="text-muted fw-semibold">{label}</span>
                    <input
                        type="number"
                        min={min}
                        step={step}
                        value={value}
                        onChange={(e) => {
                            let val = parseFloat(e.target.value);
                            if (isNaN(val)) return;
                            onChange(val);
                        }}
                        className="bg-dark text-white border-secondary rounded text-center text-xs px-1"
                        style={{ width: '65px', height: '22px', border: '1px solid rgba(255,255,255,0.2)' }}
                    />
                </div>
                <div className="d-flex align-items-center gap-2">
                    <input
                        type="range"
                        className="w-100 flex-grow-1"
                        min={min}
                        max={max}
                        step={step}
                        value={value}
                        onChange={(e) => onChange(parseFloat(e.target.value))}
                    />
                    <span className="text-muted text-xs" style={{ minWidth: '70px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {value} {suffix}
                    </span>
                </div>
            </div>
        );
    };

    // Estados de Colapsado/Expandido
    const [isExpanded, setIsExpanded] = useState(true);
    const [isContactExpanded, setIsContactExpanded] = useState(false);
    const [isCoverExpanded, setIsCoverExpanded] = useState(false);
    const [isSoporteExpanded, setIsSoporteExpanded] = useState(false);
    const [isCumpleanosExpanded, setIsCumpleanosExpanded] = useState(false);
    const [isLogoExpanded, setIsLogoExpanded] = useState(false);
    const [showLiveBadgeList, setShowLiveBadgeList] = useState(false);
    const [showLogoOverlayList, setShowLogoOverlayList] = useState(false);

    // Refs para archivos de Insignia
    const fileInputRef = useRef(null);
    const liveBadgeLogoRef = useRef(null);
    const logoFileRef = useRef(null);

    // Refs para archivos de Contacto
    const contactIconRef = useRef(null);
    const bgImageRef = useRef(null);
    const bgVideoRef = useRef(null);

    // Refs para archivos de Portada
    const coverLogoRef = useRef(null);
    const coverBgImageRef = useRef(null);
    const coverBgVideoRef = useRef(null);
    const coverTitleImgRef = useRef(null);

    // Refs para Cumpleanos
    const logoLeftRef = useRef(null);
    const logoRightRef = useRef(null);

    // --- ESTADO 1: CONFIGURACIÓN DE INSIGNIA "EN VIVO" ---
    const [config, setConfig] = useState({
        visible: true,
        expanded: false,
        designStyle: 'simple',
        scale: 1.0,
        speed: 1.5,
        position: 'center',
        color: '#ff003c',
        glow: 12,
        offsetX: 90,
        offsetY: 8,
        liveBadges: [],
        activeLiveBadge: null
    });

    const [newLiveBadgeItem, setNewLiveBadgeItem] = useState({
        text: '',
        logoDataUrl: null,
        showLogo: true
    });

    const [editingLiveBadgeItemId, setEditingLiveBadgeItemId] = useState(-1);

    const [logoConfig, setLogoConfig] = useState({
        logoDataUrl: null,
        visible: false,
        scale: 1.0,
        orientation: 'horizontal',
        position: 'top-right',
        offsetX: 90,
        offsetY: 8
    });

    // --- ESTADO 2: CONFIGURACIÓN DE CONTACTO (WHATSAPP) ---
    const [contactConfig, setContactConfig] = useState({
        visible: true,
        expanded: false,
        position: 'center',
        offsetX: 50,
        offsetY: 85,
        bgType: 'color',
        bgColor: '#b3001e',
        bgMediaUrl: null,
        innerBgColor: '#ffffff',
        innerTextColor: '#000000',
        outerTextColor: '#ffffff',
        borderColor: '#ffffff',
        borderWidth: 2,
        iconZoom: 1.0,
        outerWidth: 900,
        outerHeight: 220,
        innerWidth: 780,
        innerHeight: 92,
        scale: 1.0,
        contactos: [],
        activeContacto: null
    });

    const [showContactoList, setShowContactoList] = useState(false);
    const [newContactoItem, setNewContactoItem] = useState({
        nombreCanal: '',
        topText: 'Envía tu mensaje de voz al:',
        phoneNumber: '',
        bottomText: 'Powered by Interaktia',
        iconBgColor: '#25d366',
        iconSymbolColor: '#ffffff',
        iconDataUrl: null
    });
    const [editingContactoItemId, setEditingContactoItemId] = useState(-1);
    const contactItemLogoRef = useRef(null);

    // --- ESTADO 3: CONFIGURACIÓN DE PORTADA (COVER) ---
    const [portadaConfig, setPortadaConfig] = useState({
        titleText: 'LA TÓMBOLA PERRONA',
        subtitleText: '',
        visible: false,
        expanded: false,
        bgType: 'video',
        bgColor: '#b3001e',
        bgMediaUrl: '/img/page/backgrounds/Fondo-SelfieFan-Varonil-Cadenas-Zoom-Master-2.mp4',
        logoDataUrl: null,
        logoScale: 1.0,
        logoOffsetX: 0,
        logoOffsetY: 0,
        titleImgDataUrl: null,
        titleImgWidth: 500,
        titleImgScale: 1.0,
        titleOffsetX: 0,
        titleOffsetY: 0,
        titleSize: 4.8,
        subtitleSize: 2.2,
        showIcon: true,
        scale: 1.0
    });

    // --- ESTADO 4: CONFIGURACIÓN DE SOPORTE (DETALLES TÉCNICOS) ---
    const [soporteConfig, setSoporteConfig] = useState({
        titleText: 'DETALLES TÉCNICOS',
        titleColor: '#ffaa00',
        titleSize: 2.0,
        titleOffsetX: 0,
        titleOffsetY: -80,
        descriptionText: 'Estamos experimentando dificultades técnicas. Volveremos en breve, gracias por tu paciencia.',
        descriptionColor: '#94a3b8',
        descSize: 1.0,
        descOffsetX: 0,
        descOffsetY: 50,
        accentScale: 1.0,
        accentOffsetX: 0,
        accentOffsetY: -220,
        visible: false,
        expanded: false,
        bgColor: '#151620',
        scale: 1.0,
        speed: 1.8,
        color: '#ffaa00',
        glow: 30,
        position: 'center',
        offsetX: 960,
        offsetY: 540
    });

    // --- ESTADO 5: CONFIGURACIÓN DE CUMPLEANOS ---
    const [cumpleanosConfig, setCumpleanosConfig] = useState({
        visible: false,
        expanded: false,
        titleText: 'Felicidades',
        namesJson: '[]',
        scale: 1.0,
        position: 'center',
        offsetX: 50,
        offsetY: 85,
        fontFamily: 'Montserrat',
        titleColor: '#ffffff',
        nameColor: '#ffffff',
        bgColor: '#ff003c',
        fontSizeTitle: 2.0,
        fontSizeName: 3.2,
        logoLeftDataUrl: null,
        logoRightDataUrl: null,
        showConfetti: true,
        confettiOpacity: 0.8,
        rotationSpeed: 5,
        activeName: '',
        isPlaying: false,
        isVertical: false
    });

    // --- ESTADO 6: CONFIGURACIÓN DE MENCIONES ---
    const [mencionesConfig, setMencionesConfig] = useState({
        visible: false,
        scale: 1.0,
        logoScale: 1.0,
        logoRightScale: 1.0,
        elementSpacing: 24,
        position: 'center',
        offsetX: 50,
        offsetY: 85,
        fontFamily: 'Montserrat',
        titleColor: '#ffffff',
        textColor: '#ffffff',
        bgColor: '#ff003c',
        menciones: [],
        activeMencion: null,
        isVertical: false
    });

    const [isMencionesExpanded, setIsMencionesExpanded] = useState(false);
    const [newMencion, setNewMencion] = useState({
        nombreInterno: '',
        texto: '',
        link: '',
        formato: 'Formato A',
        logoDataUrl: null,
        logoRightDataUrl: null
    });
    const [editingMencionId, setEditingMencionId] = useState(-1);
    const mencionesLogoRef = useRef(null);
    const mencionesLogoRightRef = useRef(null);

    const [newBirthdayName, setNewBirthdayName] = useState('');
    const [editingBirthdayIndex, setEditingBirthdayIndex] = useState(-1);
    const [editingBirthdayName, setEditingBirthdayName] = useState('');
    const [showCumpleanosPlayer, setShowCumpleanosPlayer] = useState(false);
    const [showMencionesList, setShowMencionesList] = useState(false);
    const [shownBirthdays, setShownBirthdays] = useState([]);

    // --- ESTADOS DE PREGUNTAS ---
    const [preguntasConfig, setPreguntasConfig] = useState({
        visible: false,
        scale: 1.0,
        logoDataUrl: null,
        position: 'center',
        offsetX: 50,
        offsetY: 85,
        fontFamily: 'Montserrat',
        titleColor: '#ffffff',
        textColor: '#ffffff',
        bgColor: '#ff003c',
        preguntas: [],
        activePregunta: null
    });
    const [isPreguntasExpanded, setIsPreguntasExpanded] = useState(false);
    const [showPreguntasList, setShowPreguntasList] = useState(false);
    const [newPreguntaText, setNewPreguntaText] = useState('');
    const [editingPreguntaId, setEditingPreguntaId] = useState(-1);
    const [editingPreguntaText, setEditingPreguntaText] = useState('');
    const preguntasLogoRef = useRef(null);

    // --- ESTADOS DE PREMIOS ---
    const [premiosConfig, setPremiosConfig] = useState({
        visible: false,
        scale: 1.0,
        logoDataUrl: null,
        position: 'center',
        offsetX: 50,
        offsetY: 85,
        fontFamily: 'Montserrat',
        titleColor: '#ffffff',
        textColor: '#ffffff',
        bgColor: '#ff003c',
        premios: [],
        activePremio: null
    });
    const [isPremiosExpanded, setIsPremiosExpanded] = useState(false);
    const [showPremiosList, setShowPremiosList] = useState(false);
    const [newPremioText, setNewPremioText] = useState('');
    const [editingPremioId, setEditingPremioId] = useState(-1);
    const premiosLogoRef = useRef(null);

    useEffect(() => {
        if (cumpleanosConfig.visible && cumpleanosConfig.activeName) {
            setShownBirthdays(prev => {
                if (!prev.includes(cumpleanosConfig.activeName)) {
                    return [...prev, cumpleanosConfig.activeName];
                }
                return prev;
            });
        }
    }, [cumpleanosConfig.visible, cumpleanosConfig.activeName]);

    // Estados para Modales de Confirmación
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });

    // Cargar configuraciones iniciales
    useEffect(() => {
        const loadConfigs = async () => {
            setIsLoading(true);
            const resBadge = await GetTombolaConfig();
            const resContact = await GetContactoConfig();
            const resPortada = await GetPortadaConfig();
            const resSoporte = await GetSoporteConfig();
            const resCumpleanos = await GetCumpleanosConfig();
            const resMenciones = await GetMencionesConfig();
            const resPreguntas = await GetPreguntasConfig();
            const resPremios = await GetPremiosConfig();
            const resLogo = await GetLogoConfig();
            setIsLoading(false);
            
            if (resBadge.success && resBadge.data) {
                setConfig({
                    ...resBadge.data,
                    liveBadges: resBadge.data.liveBadges || resBadge.data.LiveBadges || []
                });
            }
            if (resContact.success && resContact.data) {
                setContactConfig({
                    ...resContact.data,
                    contactos: resContact.data.contactos || resContact.data.Contactos || []
                });
            }
            if (resPortada.success && resPortada.data) {
                setPortadaConfig({ ...resPortada.data });
            }
            if (resSoporte.success && resSoporte.data) {
                setSoporteConfig({ ...resSoporte.data });
            }
            if (resCumpleanos.success && resCumpleanos.data) {
                setCumpleanosConfig({ ...resCumpleanos.data });
            }
            if (resMenciones.success && resMenciones.data) {
                setMencionesConfig({ ...resMenciones.data });
            }
            if (resPreguntas.success && resPreguntas.data) {
                setPreguntasConfig({ ...resPreguntas.data });
            }
            if (resPremios.success && resPremios.data) {
                setPremiosConfig({ ...resPremios.data });
            }
            if (resLogo.success && resLogo.data) {
                setLogoConfig({ ...resLogo.data });
            }
        };
        loadConfigs();
    }, []);

    const ReturnToDashboard = () => {
        try {
            getOutOfTheDynamic();
        } catch {
            window.location.href = '/admin/dashboard';
        }
    };

    const updateTombolaConfigState = (data) => {
        if (!data) return;
        setConfig({
            ...data,
            liveBadges: data.liveBadges || data.LiveBadges || []
        });
    };

    // ==========================================================================
    // CONTROLADORES DE EVENTOS: INSIGNIA "EN VIVO"
    // ==========================================================================
    // ==========================================================================
    // CONTROLADORES DE EVENTOS: INSIGNIA "EN VIVO" (DINÁMICA MULTIELEMENTO)
    // ==========================================================================
    const handleLiveBadgeItemLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const targetSize = 150;
                    canvas.width = targetSize;
                    canvas.height = targetSize;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        const sourceSize = Math.min(img.width, img.height);
                        const sx = (img.width - sourceSize) / 2;
                        const sy = (img.height - sourceSize) / 2;
                        ctx.drawImage(img, sx, sy, sourceSize, sourceSize, 0, 0, targetSize, targetSize);
                        const compressed = canvas.toDataURL('image/png');
                        setNewLiveBadgeItem(prev => ({ ...prev, logoDataUrl: compressed }));
                        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logo de insignia cargado.' });
                    }
                };
                img.src = evt.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLiveBadgeItemLogoReset = () => {
        setNewLiveBadgeItem(prev => ({ ...prev, logoDataUrl: null }));
        if (liveBadgeLogoRef.current) liveBadgeLogoRef.current.value = '';
    };

    const handleLiveBadgeAdd = async () => {
        if (!newLiveBadgeItem.text.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El texto de la insignia no puede estar vacío.' });
            return;
        }

        setIsLoading(true);
        if (editingLiveBadgeItemId !== -1) {
            const res = await UpdateLiveBadgeItem(editingLiveBadgeItemId, newLiveBadgeItem);
            setIsLoading(false);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Insignia actualizada con éxito.' });
                setEditingLiveBadgeItemId(-1);
                setNewLiveBadgeItem({ text: '', logoDataUrl: null, showLogo: true });
                const r = await GetTombolaConfig();
                if (r.success) updateTombolaConfigState(r.data);
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al actualizar insignia.' });
            }
        } else {
            const res = await CreateLiveBadgeItem(newLiveBadgeItem);
            setIsLoading(false);
            if (res.success) {
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Insignia creada con éxito.' });
                setNewLiveBadgeItem({ text: '', logoDataUrl: null, showLogo: true });
                const r = await GetTombolaConfig();
                if (r.success) updateTombolaConfigState(r.data);
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al crear insignia.' });
            }
        }
    };

    const handleLiveBadgeSelect = async (id, isCurrentlyActive) => {
        if (isCurrentlyActive && config.visible) {
            setIsLoading(true);
            const res = await HideTombolaOverlay();
            setIsLoading(false);
            if (res.success) {
                setConfig(prev => ({ ...prev, visible: false }));
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Insignia oculta de pantalla.' });
            }
        } else {
            setIsLoading(true);
            const resSelect = await SelectLiveBadgeItem(id);
            if (resSelect.success) {
                const resVis = await ShowTombolaOverlay();
                setIsLoading(false);
                if (resVis.success) {
                    const r = await GetTombolaConfig();
                    if (r.success) updateTombolaConfigState(r.data);
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Insignia activa mostrada en pantalla.' });
                }
            } else {
                setIsLoading(false);
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al seleccionar insignia.' });
            }
        }
    };

    const handleLiveBadgeDelete = async (id) => {
        setIsLoading(true);
        const res = await DeleteLiveBadgeItem(id);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Insignia eliminada con éxito.' });
            const r = await GetTombolaConfig();
            if (r.success) updateTombolaConfigState(r.data);
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al eliminar insignia.' });
        }
    };

    const handleLiveBadgeMove = async (index, direction) => {
        if (!config.liveBadges) return;
        const list = [...config.liveBadges];
        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        if (targetIndex < 0 || targetIndex >= list.length) return;

        setIsLoading(true);
        const temp = list[index];
        list[index] = list[targetIndex];
        list[targetIndex] = temp;

        for (let i = 0; i < list.length; i++) {
            await UpdateLiveBadgeItem(list[i].id || list[i].Id, {
                text: list[i].text,
                logoDataUrl: list[i].logoDataUrl,
                showLogo: list[i].showLogo,
                position: i
            });
        }
        setIsLoading(false);
        const r = await GetTombolaConfig();
        if (r.success) updateTombolaConfigState(r.data);
    };

    const handleToggleLiveBadgeLogo = async (id) => {
        setIsLoading(true);
        const res = await ToggleLiveBadgeLogo(id);
        setIsLoading(false);
        if (res.success) {
            const r = await GetTombolaConfig();
            if (r.success) updateTombolaConfigState(r.data);
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Visibilidad de logo de insignia actualizada.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de logo.' });
        }
    };

    const handleConfigChange = (field, val) => {
        setConfig(prev => ({ ...prev, [field]: val }));
    };

    const handleStyleChange = (style) => {
        setConfig(prev => ({ ...prev, designStyle: style }));
    };

    const handlePositionChange = (pos) => {
        let x = 0;
        let y = 0;
        switch (pos) {
            case 'top-left':
                x = 10;
                y = 8;
                break;
            case 'top-right':
                x = 90;
                y = 8;
                break;
            case 'top-center':
                x = 50;
                y = 8;
                break;
            case 'center':
                x = 50;
                y = 50;
                break;
            default:
                x = 10;
                y = 8;
        }
        setConfig(prev => ({
            ...prev,
            position: pos,
            offsetX: x,
            offsetY: y
        }));
    };

    const handleSaveConfig = async () => {
        setIsLoading(true);
        const res = await SetTombolaConfig(config);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes de Insignia guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar la insignia.' });
        }
    };

    const triggerToggleOverlayVisibility = async (nextState) => {
        setIsLoading(true);
        const res = nextState ? await ShowTombolaOverlay() : await HideTombolaOverlay();
        setIsLoading(false);
        if (res.success) {
            setConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Insignia visible en pantalla.' : 'Insignia oculta.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de insignia.' });
        }
    };

    // ==========================================================================
    // CONTROLADORES DE EVENTOS: LOGOTIPO OVERLAY
    // ==========================================================================
    const handleLogoConfigChange = (field, val) => {
        setLogoConfig(prev => ({ ...prev, [field]: val }));
    };

    const handleLogoPositionChange = (pos) => {
        let x = 0;
        let y = 0;
        switch (pos) {
            case 'top-left':
                x = 10;
                y = 8;
                break;
            case 'top-right':
                x = 90;
                y = 8;
                break;
            case 'bottom-left':
                x = 10;
                y = 92;
                break;
            case 'bottom-right':
                x = 90;
                y = 92;
                break;
            case 'center':
                x = 50;
                y = 50;
                break;
            default:
                x = 90;
                y = 8;
        }
        setLogoConfig(prev => ({
            ...prev,
            position: pos,
            offsetX: x,
            offsetY: y
        }));
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setLogoConfig(prev => ({ ...prev, logoDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logotipo cargado.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoReset = () => {
        setLogoConfig(prev => ({ ...prev, logoDataUrl: null }));
        if (logoFileRef.current) logoFileRef.current.value = '';
    };

    const handleSaveLogoConfig = async () => {
        setIsLoading(true);
        const res = await SetLogoConfig(logoConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes de Logotipo guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar el logotipo.' });
        }
    };

    const triggerToggleLogoVisibility = async (nextState) => {
        setIsLoading(true);
        const res = await ToggleLogoVisibility(nextState);
        setIsLoading(false);
        if (res.success) {
            setLogoConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Logotipo visible en pantalla.' : 'Logotipo oculto.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad del logotipo.' });
        }
    };


    // ==========================================================================
    // CONTROLADORES DE EVENTOS: CONTACTO (WHATSAPP)
    // ==========================================================================
    // ==========================================================================
    // CONTROLADORES DE EVENTOS: CONTACTO (WHATSAPP MULTICANAL)
    // ==========================================================================
    const handleContactoSelect = async (id, isCurrentlyActive) => {
        if (isCurrentlyActive && contactConfig.visible) {
            setIsLoading(true);
            const res = await ToggleContactoVisibility(false);
            setIsLoading(false);
            if (res.success) {
                setContactConfig(prev => ({ ...prev, visible: false }));
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Contacto oculto de pantalla.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al ocultar contacto.' });
            }
        } else {
            setIsLoading(true);
            const resSelect = await SelectContactoItem(id);
            if (resSelect.success) {
                const resVis = await ToggleContactoVisibility(true);
                setIsLoading(false);
                if (resVis.success) {
                    setContactConfig(prev => {
                        const updatedList = prev.contactos.map(c => ({ ...c, isActive: (c.id || c.Id) === id }));
                        const active = updatedList.find(c => (c.id || c.Id) === id);
                        return {
                            ...prev,
                            visible: true,
                            contactos: updatedList,
                            activeContacto: active
                        };
                    });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Contacto visible en pantalla.' });
                }
            } else {
                setIsLoading(false);
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al seleccionar contacto.' });
            }
        }
    };

    const handleContactoAdd = async () => {
        if (!newContactoItem.nombreCanal.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El nombre del canal es obligatorio.' });
            return;
        }
        if (!newContactoItem.phoneNumber.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El número/contacto es obligatorio.' });
            return;
        }

        setIsLoading(true);
        if (editingContactoItemId !== -1) {
            const current = contactConfig.contactos.find(c => (c.id || c.Id) === editingContactoItemId);
            const updated = { ...current, ...newContactoItem };
            const res = await UpdateContactoItem(editingContactoItemId, updated);
            setIsLoading(false);
            if (res.success) {
                setContactConfig(prev => {
                    const list = prev.contactos.map(c => (c.id || c.Id) === editingContactoItemId ? { ...c, ...newContactoItem } : c);
                    const active = prev.activeContacto && (prev.activeContacto.id || prev.activeContacto.Id) === editingContactoItemId
                        ? { ...prev.activeContacto, ...newContactoItem }
                        : prev.activeContacto;
                    return { ...prev, contactos: list, activeContacto: active };
                });
                setEditingContactoItemId(-1);
                setNewContactoItem({
                    nombreCanal: '',
                    topText: 'Envía tu mensaje de voz al:',
                    phoneNumber: '',
                    bottomText: 'Powered by Interaktia',
                    iconBgColor: '#25d366',
                    iconSymbolColor: '#ffffff',
                    iconDataUrl: null
                });
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Contacto actualizado con éxito.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al actualizar contacto.' });
            }
        } else {
            const res = await CreateContactoItem(newContactoItem);
            setIsLoading(false);
            if (res.success && res.data) {
                setContactConfig(prev => {
                    const list = [...prev.contactos, res.data];
                    return { ...prev, contactos: list };
                });
                setNewContactoItem({
                    nombreCanal: '',
                    topText: 'Envía tu mensaje de voz al:',
                    phoneNumber: '',
                    bottomText: 'Powered by Interaktia',
                    iconBgColor: '#25d366',
                    iconSymbolColor: '#ffffff',
                    iconDataUrl: null
                });
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Contacto creado con éxito.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al guardar contacto.' });
            }
        }
    };

    const handleContactoDelete = async (id) => {
        setIsLoading(true);
        const res = await DeleteContactoItem(id);
        setIsLoading(false);
        if (res.success) {
            setContactConfig(prev => {
                const list = prev.contactos.filter(c => (c.id || c.Id) !== id);
                let active = prev.activeContacto;
                let visible = prev.visible;
                if (active && (active.id || active.Id) === id) {
                    active = list.length > 0 ? list[0] : null;
                    if (list.length > 0) {
                        list[0].isActive = true;
                    } else {
                        visible = false;
                    }
                }
                return { ...prev, contactos: list, activeContacto: active, visible };
            });
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'Contacto eliminado.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al eliminar contacto.' });
        }
    };

    const handleContactoMove = async (index, direction) => {
        const list = [...contactConfig.contactos];
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === list.length - 1) return;

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        const temp = list[index];
        list[index] = list[targetIndex];
        list[targetIndex] = temp;

        list[index].position = index;
        list[targetIndex].position = targetIndex;

        setIsLoading(true);
        const res1 = await UpdateContactoItem(list[index].id || list[index].Id, list[index]);
        const res2 = await UpdateContactoItem(list[targetIndex].id || list[targetIndex].Id, list[targetIndex]);
        setIsLoading(false);

        if (res1.success && res2.success) {
            setContactConfig(prev => ({ ...prev, contactos: list }));
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar orden de contactos.' });
        }
    };

    const handleContactoItemLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setNewContactoItem(prev => ({ ...prev, iconDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logo de canal cargado.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleContactoItemLogoReset = () => {
        setNewContactoItem(prev => ({ ...prev, iconDataUrl: null }));
        if (contactItemLogoRef.current) contactItemLogoRef.current.value = '';
    };

    const handleContactStyleChange = (field, val) => {
        setContactConfig(prev => ({ ...prev, [field]: val }));
    };

    const handleContactPositionChange = (pos) => {
        let x = 50;
        let y = 85;
        switch (pos) {
            case 'top':
                y = 10;
                break;
            case 'center':
                y = 50;
                break;
            case 'bottom':
                y = 85;
                break;
            default:
                y = 85;
        }
        setContactConfig(prev => ({
            ...prev,
            position: pos,
            offsetX: x,
            offsetY: y
        }));
    };

    // Subidas de fondo (Imagen y Video)
    const handleBgMediaUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 8 * 1024 * 1024) {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'El archivo excede el límite de 8MB.' });
                return;
            }
            const reader = new FileReader();
            reader.onload = (evt) => {
                setContactConfig(prev => ({ ...prev, bgMediaUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `${type === 'image' ? 'Imagen' : 'Video'} de fondo cargado.` });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleBgMediaReset = () => {
        setContactConfig(prev => ({ ...prev, bgMediaUrl: null }));
        if (bgImageRef.current) bgImageRef.current.value = '';
        if (bgVideoRef.current) bgVideoRef.current.value = '';
    };

    const handleSaveContactConfig = async () => {
        setIsLoading(true);
        const res = await SetContactoConfig(contactConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes estéticos de Contacto guardados.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar la configuración.' });
        }
    };

    const triggerToggleContactVisibility = async (nextState) => {
        setIsLoading(true);
        const res = await ToggleContactoVisibility(nextState);
        setIsLoading(false);
        if (res.success) {
            setContactConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Contacto visible en pantalla.' : 'Contacto oculto de pantalla.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de contacto.' });
        }
    };

    // ==========================================================================
    // CONTROLADORES DE EVENTOS: PORTADA (COVER)
    // ==========================================================================
    const handlePortadaTextChange = (field, val) => {
        setPortadaConfig(prev => ({ ...prev, [field]: val }));
    };

    const handlePortadaLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const targetSize = 250; // optimized size for cover logo
                    const scaleFactor = Math.min(targetSize / img.width, targetSize / img.height, 1);
                    canvas.width = img.width * scaleFactor;
                    canvas.height = img.height * scaleFactor;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        const compressed = canvas.toDataURL('image/png');
                        setPortadaConfig(prev => ({ ...prev, logoDataUrl: compressed }));
                        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logo de Portada cargado y optimizado.' });
                    }
                };
                img.src = evt.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePortadaLogoReset = () => {
        setPortadaConfig(prev => ({ ...prev, logoDataUrl: null }));
        if (coverLogoRef.current) coverLogoRef.current.value = '';
    };

    const handlePortadaTitleImgUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    const targetWidth = 1000; // optimized size for title image
                    const scaleFactor = Math.min(targetWidth / img.width, 1);
                    canvas.width = img.width * scaleFactor;
                    canvas.height = img.height * scaleFactor;
                    const ctx = canvas.getContext('2d');
                    if (ctx) {
                        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                        const compressed = canvas.toDataURL('image/png');
                        setPortadaConfig(prev => ({ ...prev, titleImgDataUrl: compressed }));
                        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen de título cargada con éxito.' });
                    }
                };
                img.src = evt.target.result;
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePortadaTitleImgReset = () => {
        setPortadaConfig(prev => ({ ...prev, titleImgDataUrl: null }));
        if (coverTitleImgRef.current) coverTitleImgRef.current.value = '';
    };

    const handlePortadaBgUpload = (e, type) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 12 * 1024 * 1024) { // 12MB limit for cover video/image
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'El archivo excede el límite de 12MB.' });
                return;
            }
            const reader = new FileReader();
            reader.onload = (evt) => {
                setPortadaConfig(prev => ({ ...prev, bgMediaUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Fondo de Portada (${type === 'image' ? 'Imagen' : 'Video'}) cargado.` });
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePortadaBgReset = () => {
        setPortadaConfig(prev => ({ ...prev, bgMediaUrl: null }));
        if (coverBgImageRef.current) coverBgImageRef.current.value = '';
        if (coverBgVideoRef.current) coverBgVideoRef.current.value = '';
    };

    const handleSavePortadaConfig = async () => {
        setIsLoading(true);
        const res = await SetPortadaConfig(portadaConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes de Portada guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar la portada.' });
        }
    };

    const triggerTogglePortadaVisibility = async (nextState) => {
        setIsLoading(true);
        const res = nextState ? await ShowPortadaOverlay() : await HidePortadaOverlay();
        setIsLoading(false);
        if (res.success) {
            setPortadaConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Portada visible en pantalla.' : 'Portada oculta.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de la portada.' });
        }
    };

    const handleSoporteTextChange = (field, val) => {
        setSoporteConfig(prev => ({ ...prev, [field]: val }));
    };

    const handleSoportePositionChange = (pos) => {
        let x = 960;
        let y = 540;
        switch (pos) {
            case 'top':
                x = 960;
                y = 270;
                break;
            case 'bottom':
                x = 960;
                y = 810;
                break;
            case 'center':
            default:
                x = 960;
                y = 540;
                break;
        }
        setSoporteConfig(prev => ({ ...prev, position: pos, offsetX: x, offsetY: y }));
    };

    const handleSaveSoporteConfig = async () => {
        setIsLoading(true);
        const res = await SetSoporteConfig(soporteConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes de Soporte guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar el soporte.' });
        }
    };

    const triggerToggleSoporteVisibility = async (nextState) => {
        setIsLoading(true);
        const res = nextState ? await ShowSoporteOverlay() : await HideSoporteOverlay();
        setIsLoading(false);
        if (res.success) {
            setSoporteConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Pantalla técnica visible en pantalla.' : 'Pantalla técnica oculta.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de la pantalla técnica.' });
        }
    };

    const handleCumpleanosTextChange = (field, val) => {
        setCumpleanosConfig(prev => ({ ...prev, [field]: val }));
    };

    const handleCumpleanosPositionChange = (pos) => {
        let x = 50;
        let y = 85;
        switch (pos) {
            case 'top':
                y = 10;
                break;
            case 'center':
                y = 50;
                break;
            case 'bottom':
                y = 85;
                break;
            default:
                y = 85;
        }
        setCumpleanosConfig(prev => ({ ...prev, position: pos, offsetX: x, offsetY: y }));
    };

    // --- REPRODUCTOR DE CUMPLEANOS ---
    const updateCumpleanosConfigAndBroadcast = async (updatedFields) => {
        setCumpleanosConfig(prev => {
            const merged = { ...prev, ...updatedFields };
            SetCumpleanosConfig(merged);
            return merged;
        });
    };

    useEffect(() => {
        if (!cumpleanosConfig.isPlaying || !cumpleanosConfig.visible) return;
        const names = getBirthdayList();
        if (names.length <= 1) return;

        const interval = setInterval(() => {
            const currentIdx = names.indexOf(cumpleanosConfig.activeName);
            const nextIdx = currentIdx === -1 ? 0 : (currentIdx + 1) % names.length;
            const nextName = names[nextIdx];
            updateCumpleanosConfigAndBroadcast({ activeName: nextName });
        }, (cumpleanosConfig.rotationSpeed || 5) * 1000);

        return () => clearInterval(interval);
    }, [cumpleanosConfig.isPlaying, cumpleanosConfig.visible, cumpleanosConfig.activeName, cumpleanosConfig.rotationSpeed, cumpleanosConfig.namesJson]);

    const handlePlayerPlay = async () => {
        const names = getBirthdayList();
        if (names.length === 0) {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Agrega al menos un cumpleañero para reproducir.' });
            return;
        }
        const initName = cumpleanosConfig.activeName || names[0] || '';
        
        let targetVisible = cumpleanosConfig.visible;
        if (!targetVisible) {
            setIsLoading(true);
            const res = await ShowCumpleanosOverlay();
            setIsLoading(false);
            if (res.success) {
                targetVisible = true;
            }
        }

        updateCumpleanosConfigAndBroadcast({ isPlaying: true, activeName: initName, visible: targetVisible });
        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Reproducción iniciada.' });
    };

    const handlePlayerPause = () => {
        updateCumpleanosConfigAndBroadcast({ isPlaying: false });
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reproducción pausada.' });
    };

    const handleSelectAndPlayBirthday = (name) => {
        updateCumpleanosConfigAndBroadcast({ isPlaying: false, activeName: name });
        showMessage({ type: MESSAGE_TYPE.INFO, text: `Seleccionado: ${name}` });
    };

    const handlePlayerNext = () => {
        const names = getBirthdayList();
        if (names.length === 0) return;
        const currentIdx = names.indexOf(cumpleanosConfig.activeName);
        const nextIdx = currentIdx === -1 ? 0 : (currentIdx + 1) % names.length;
        const nextName = names[nextIdx];
        updateCumpleanosConfigAndBroadcast({ activeName: nextName });
    };

    const handlePlayerPrev = () => {
        const names = getBirthdayList();
        if (names.length === 0) return;
        const currentIdx = names.indexOf(cumpleanosConfig.activeName);
        const prevIdx = currentIdx <= 0 ? names.length - 1 : currentIdx - 1;
        const prevName = names[prevIdx];
        updateCumpleanosConfigAndBroadcast({ activeName: prevName });
    };

    const handleSaveCumpleanosConfig = async () => {
        setIsLoading(true);
        const res = await SetCumpleanosConfig(cumpleanosConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes de Cumpleañeros guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: res.message || 'Error al guardar la configuración de cumpleañeros.' });
        }
    };

    const triggerToggleCumpleanosVisibility = async (nextState) => {
        setIsLoading(true);
        const res = nextState ? await ShowCumpleanosOverlay() : await HideCumpleanosOverlay();
        setIsLoading(false);
        if (res.success) {
            setCumpleanosConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({ 
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                text: nextState ? 'Pantalla de cumpleañeros visible en pantalla.' : 'Pantalla de cumpleañeros oculta.' 
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de cumpleañeros.' });
        }
    };

    const getBirthdayList = () => {
        try {
            return JSON.parse(cumpleanosConfig.namesJson || '[]');
        } catch {
            return [];
        }
    };

    const updateBirthdayList = (newList) => {
        setCumpleanosConfig(prev => ({
            ...prev,
            namesJson: JSON.stringify(newList)
        }));
        if (newList.length === 0) {
            setShownBirthdays([]);
        }
    };

    const handleAddBirthday = () => {
        if (!newBirthdayName.trim()) return;
        const currentList = getBirthdayList();
        const newList = [...currentList, newBirthdayName.trim()];
        updateBirthdayList(newList);
        setNewBirthdayName('');
    };

    const handleMoveBirthday = (index, direction) => {
        const currentList = getBirthdayList();
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === currentList.length - 1) return;

        const targetIndex = direction === 'up' ? index - 1 : index + 1;
        const temp = currentList[index];
        currentList[index] = currentList[targetIndex];
        currentList[targetIndex] = temp;

        updateBirthdayList(currentList);
    };

    const handleStartEditBirthday = (index) => {
        const currentList = getBirthdayList();
        setEditingBirthdayIndex(index);
        setEditingBirthdayName(currentList[index]);
    };

    const handleSaveEditBirthday = () => {
        if (!editingBirthdayName.trim()) return;
        const currentList = getBirthdayList();
        currentList[editingBirthdayIndex] = editingBirthdayName.trim();
        updateBirthdayList(currentList);
        setEditingBirthdayIndex(-1);
        setEditingBirthdayName('');
    };

    const handleDeleteBirthday = (index) => {
        const currentList = getBirthdayList();
        const newList = currentList.filter((_, i) => i !== index);
        updateBirthdayList(newList);
    };

    const handleLogoLeftUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setCumpleanosConfig(prev => ({ ...prev, logoLeftDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logo izquierdo cargado.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoRightUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setCumpleanosConfig(prev => ({ ...prev, logoRightDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Logo derecho cargado.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleLogoLeftReset = () => {
        setCumpleanosConfig(prev => ({ ...prev, logoLeftDataUrl: null }));
        if (logoLeftRef.current) logoLeftRef.current.value = '';
    };

    const handleLogoRightReset = () => {
        setCumpleanosConfig(prev => ({ ...prev, logoRightDataUrl: null }));
        if (logoRightRef.current) logoRightRef.current.value = '';
    };

    // ==========================================================================
    // CONTROLADORES DE EVENTOS: PANTALLA DE MENCIONES
    // ==========================================================================
    const handleMencionesVisibilityToggle = async () => {
        setIsLoading(true);
        const nextState = !mencionesConfig.visible;
        const res = await ToggleMencionesVisibility(nextState);
        setIsLoading(false);
        if (res.success) {
            setMencionesConfig(prev => ({ ...prev, visible: nextState }));
            showMessage({
                type: nextState ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO,
                text: nextState ? 'Menciones visibles en pantalla.' : 'Menciones ocultas.'
            });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de menciones.' });
        }
    };

    const handleMencionSelect = async (id) => {
        setIsLoading(true);
        const res = await SelectMencion(id);
        setIsLoading(false);
        if (res.success) {
            let nextVisible = mencionesConfig.visible;
            if (!nextVisible) {
                setIsLoading(true);
                const toggleRes = await ToggleMencionesVisibility(true);
                setIsLoading(false);
                if (toggleRes.success) {
                    nextVisible = true;
                }
            }
            setMencionesConfig(prev => ({
                ...prev,
                visible: nextVisible,
                activeMencion: prev.menciones.find(m => m.id === id),
                menciones: prev.menciones.map(m => ({ ...m, isActive: m.id === id }))
            }));
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mención seleccionada para transmitir.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al seleccionar mención.' });
        }
    };

    const handleMencionToggleSelect = async (id, isCurrentlyActive) => {
        if (isCurrentlyActive && mencionesConfig.visible) {
            setIsLoading(true);
            const res = await ToggleMencionesVisibility(false);
            setIsLoading(false);
            if (res.success) {
                setMencionesConfig(prev => ({ ...prev, visible: false }));
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Menciones ocultadas de pantalla.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al ocultar menciones.' });
            }
        } else {
            await handleMencionSelect(id);
        }
    };

    const handleMencionAdd = async () => {
        if (!newMencion.nombreInterno.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El nombre interno es obligatorio.' });
            return;
        }
        setIsLoading(true);
        if (editingMencionId !== -1) {
            const res = await UpdateMencion(editingMencionId, newMencion);
            setIsLoading(false);
            if (res.success) {
                setMencionesConfig(prev => ({
                    ...prev,
                    menciones: prev.menciones.map(m => m.id === editingMencionId ? { ...m, ...newMencion } : m),
                    activeMencion: prev.activeMencion?.id === editingMencionId ? { ...prev.activeMencion, ...newMencion } : prev.activeMencion
                }));
                setEditingMencionId(-1);
                setNewMencion({ nombreInterno: '', texto: '', link: '', formato: 'Formato A', logoDataUrl: null, logoRightDataUrl: null });
                if (mencionesLogoRef.current) mencionesLogoRef.current.value = '';
                if (mencionesLogoRightRef.current) mencionesLogoRightRef.current.value = '';
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mención actualizada con éxito.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al actualizar mención.' });
            }
        } else {
            const res = await CreateMencion(newMencion);
            setIsLoading(false);
            if (res.success && res.data) {
                setMencionesConfig(prev => ({
                    ...prev,
                    menciones: [res.data, ...prev.menciones]
                }));
                setNewMencion({ nombreInterno: '', texto: '', link: '', formato: 'Formato A', logoDataUrl: null, logoRightDataUrl: null });
                if (mencionesLogoRef.current) mencionesLogoRef.current.value = '';
                if (mencionesLogoRightRef.current) mencionesLogoRightRef.current.value = '';
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mención agregada con éxito.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al crear mención.' });
            }
        }
    };

    const handleMencionEdit = (mencion) => {
        setEditingMencionId(mencion.id);
        setNewMencion({
            nombreInterno: mencion.nombreInterno,
            texto: mencion.texto || '',
            link: mencion.link || '',
            formato: mencion.formato,
            logoDataUrl: mencion.logoDataUrl,
            logoRightDataUrl: mencion.logoRightDataUrl
        });
        setIsMencionesExpanded(true);
    };

    const handleMencionDelete = async (id) => {
        setConfirmConfig({
            message: '¿Estás seguro de que deseas eliminar esta mención?',
            textYes: 'Sí, eliminar',
            textNo: 'Cancelar',
            action: async () => {
                setIsLoading(true);
                const res = await DeleteMencion(id);
                setIsLoading(false);
                if (res.success) {
                    setMencionesConfig(prev => ({
                        ...prev,
                        menciones: prev.menciones.filter(m => m.id !== id),
                        activeMencion: prev.activeMencion?.id === id ? null : prev.activeMencion
                    }));
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Mención eliminada.' });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al eliminar mención.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const handleMencionLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setNewMencion(prev => ({ ...prev, logoDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen izquierda cargada.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMencionLogoRightUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setNewMencion(prev => ({ ...prev, logoRightDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen derecha cargada.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleMencionesSaveGeneral = async () => {
        setIsLoading(true);
        const res = await SetMencionesConfig(mencionesConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes generales de menciones guardados con éxito.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al guardar ajustes generales.' });
        }
    };

    // --- MANEJADORES DE PREGUNTAS ---
    const handlePreguntasSaveGeneral = async () => {
        setIsLoading(true);
        const res = await SetPreguntasConfig(preguntasConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes generales de preguntas guardados.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al guardar ajustes generales.' });
        }
    };

    const handlePreguntaSelect = async (id, isCurrentlyActive) => {
        if (isCurrentlyActive && preguntasConfig.visible) {
            setIsLoading(true);
            const res = await TogglePreguntasVisibility(false);
            setIsLoading(false);
            if (res.success) {
                setPreguntasConfig(prev => ({ ...prev, visible: false }));
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Preguntas ocultas de pantalla.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al ocultar preguntas.' });
            }
        } else {
            setIsLoading(true);
            const resSelect = await SelectPregunta(id);
            if (resSelect.success) {
                const resVis = await TogglePreguntasVisibility(true);
                setIsLoading(false);
                if (resVis.success) {
                    setPreguntasConfig(prev => {
                        const updatedList = prev.preguntas.map(p => ({ ...p, isActive: (p.Id || p.id) === id }));
                        const active = updatedList.find(p => (p.Id || p.id) === id);
                        return {
                            ...prev,
                            visible: true,
                            preguntas: updatedList,
                            activePregunta: active
                        };
                    });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Preguntas visibles en pantalla.' });
                }
            } else {
                setIsLoading(false);
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al seleccionar pregunta.' });
            }
        }
    };

    const handlePreguntaAdd = async () => {
        if (!newPreguntaText.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El texto de la pregunta es obligatorio.' });
            return;
        }
        setIsLoading(true);
        if (editingPreguntaId !== -1) {
            const current = preguntasConfig.preguntas.find(p => (p.id || p.Id) === editingPreguntaId);
            const updated = { ...current, texto: newPreguntaText };
            const res = await UpdatePregunta(editingPreguntaId, updated);
            setIsLoading(false);
            if (res.success) {
                setPreguntasConfig(prev => {
                    const list = prev.preguntas.map(p => ((p.id || p.Id) === editingPreguntaId) ? { ...p, texto: newPreguntaText } : p);
                    const active = (prev.activePregunta?.id || prev.activePregunta?.Id) === editingPreguntaId
                        ? { ...prev.activePregunta, texto: newPreguntaText }
                        : prev.activePregunta;
                    return { ...prev, preguntas: list, activePregunta: active };
                });
                setEditingPreguntaId(-1);
                setNewPreguntaText('');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta actualizada.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al actualizar.' });
            }
        } else {
            const res = await CreatePregunta({ texto: newPreguntaText });
            setIsLoading(false);
            if (res.success && res.data) {
                setPreguntasConfig(prev => ({
                    ...prev,
                    preguntas: [...prev.preguntas, res.data]
                }));
                setNewPreguntaText('');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta agregada.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al crear pregunta.' });
            }
        }
    };

    const handlePreguntaEdit = (pregunta) => {
        setEditingPreguntaId(pregunta.id || pregunta.Id);
        setNewPreguntaText(pregunta.texto);
    };

    const handlePreguntaDelete = async (id) => {
        setConfirmConfig({
            message: '¿Estás seguro de que deseas eliminar esta pregunta?',
            textYes: 'Sí, eliminar',
            textNo: 'Cancelar',
            action: async () => {
                setIsLoading(true);
                const res = await DeletePregunta(id);
                setIsLoading(false);
                if (res.success) {
                    setPreguntasConfig(prev => {
                        const list = prev.preguntas.filter(p => p.id !== id && p.Id !== id);
                        const isActiveDeleted = (prev.activePregunta?.id || prev.activePregunta?.Id) === id;
                        return {
                            ...prev,
                            preguntas: list,
                            activePregunta: isActiveDeleted ? null : prev.activePregunta
                        };
                    });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Pregunta eliminada.' });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al eliminar.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const handlePreguntaLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setPreguntasConfig(prev => ({ ...prev, logoDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen cargada en previsualización local.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePreguntaLogoDelete = () => {
        setPreguntasConfig(prev => ({ ...prev, logoDataUrl: null }));
        if (preguntasLogoRef.current) preguntasLogoRef.current.value = '';
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Imagen superior removida.' });
    };

    const handlePreguntaMoveUp = async (index) => {
        if (index === 0) return;
        const list = [...preguntasConfig.preguntas];
        // Swap positions
        const temp = list[index];
        list[index] = list[index - 1];
        list[index - 1] = temp;

        // Reassign position values
        list.forEach((p, idx) => { p.position = idx; p.Position = idx; });

        setPreguntasConfig(prev => ({ ...prev, preguntas: list }));
        // Save swaps to database
        setIsLoading(true);
        await UpdatePregunta(list[index].id || list[index].Id, list[index]);
        await UpdatePregunta(list[index - 1].id || list[index - 1].Id, list[index - 1]);
        setIsLoading(false);
    };

    const handlePreguntaMoveDown = async (index) => {
        const list = [...preguntasConfig.preguntas];
        if (index === list.length - 1) return;
        // Swap positions
        const temp = list[index];
        list[index] = list[index + 1];
        list[index + 1] = temp;

        // Reassign position values
        list.forEach((p, idx) => { p.position = idx; p.Position = idx; });

        setPreguntasConfig(prev => ({ ...prev, preguntas: list }));
        // Save swaps to database
        setIsLoading(true);
        await UpdatePregunta(list[index].id || list[index].Id, list[index]);
        await UpdatePregunta(list[index + 1].id || list[index + 1].Id, list[index + 1]);
        setIsLoading(false);
    };

    const handlePreguntasPrev = async () => {
        const list = preguntasConfig.preguntas;
        if (list.length === 0) return;
        const currentActive = list.find(p => p.isActive);
        let nextIdx = list.length - 1;
        if (currentActive) {
            const currentIdx = list.indexOf(currentActive);
            nextIdx = currentIdx === 0 ? list.length - 1 : currentIdx - 1;
        }
        await handlePreguntaSelect(list[nextIdx].id || list[nextIdx].Id, false);
    };

    const handlePreguntasNext = async () => {
        const list = preguntasConfig.preguntas;
        if (list.length === 0) return;
        const currentActive = list.find(p => p.isActive);
        let nextIdx = 0;
        if (currentActive) {
            const currentIdx = list.indexOf(currentActive);
            nextIdx = (currentIdx + 1) % list.length;
        }
        await handlePreguntaSelect(list[nextIdx].id || list[nextIdx].Id, false);
    };

    // --- MANEJADORES DE PREMIOS ---
    const handlePremiosSaveGeneral = async () => {
        setIsLoading(true);
        const res = await SetPremiosConfig(premiosConfig);
        setIsLoading(false);
        if (res.success) {
            showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ajustes generales de premios guardados.' });
        } else {
            showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al guardar ajustes generales.' });
        }
    };

    const handlePremioSelect = async (id, isCurrentlyActive) => {
        if (isCurrentlyActive && premiosConfig.visible) {
            setIsLoading(true);
            const res = await TogglePremiosVisibility(false);
            setIsLoading(false);
            if (res.success) {
                setPremiosConfig(prev => ({ ...prev, visible: false }));
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Premios ocultas de pantalla.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al ocultar premios.' });
            }
        } else {
            setIsLoading(true);
            const resSelect = await SelectPremio(id);
            if (resSelect.success) {
                const resVis = await TogglePremiosVisibility(true);
                setIsLoading(false);
                if (resVis.success) {
                    setPremiosConfig(prev => {
                        const updatedList = prev.premios.map(p => ({ ...p, isActive: (p.Id || p.id) === id }));
                        const active = updatedList.find(p => (p.Id || p.id) === id);
                        return {
                            ...prev,
                            visible: true,
                            premios: updatedList,
                            activePremio: active
                        };
                    });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Premios visibles en pantalla.' });
                }
            } else {
                setIsLoading(false);
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al seleccionar premio.' });
            }
        }
    };

    const handlePremioAdd = async () => {
        if (!newPremioText.trim()) {
            showMessage({ type: MESSAGE_TYPE.WARNING, text: 'El texto del premio es obligatorio.' });
            return;
        }
        setIsLoading(true);
        if (editingPremioId !== -1) {
            const current = premiosConfig.premios.find(p => (p.id || p.Id) === editingPremioId);
            const updated = { ...current, texto: newPremioText };
            const res = await UpdatePremio(editingPremioId, updated);
            setIsLoading(false);
            if (res.success) {
                setPremiosConfig(prev => {
                    const list = prev.premios.map(p => ((p.id || p.Id) === editingPremioId) ? { ...p, texto: newPremioText } : p);
                    const active = (prev.activePremio?.id || prev.activePremio?.Id) === editingPremioId
                        ? { ...prev.activePremio, texto: newPremioText }
                        : prev.activePremio;
                    return { ...prev, premios: list, activePremio: active };
                });
                setEditingPremioId(-1);
                setNewPremioText('');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Premio actualizado.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al actualizar.' });
            }
        } else {
            const res = await CreatePremio({ texto: newPremioText });
            setIsLoading(false);
            if (res.success && res.data) {
                setPremiosConfig(prev => ({
                    ...prev,
                    premios: [...prev.premios, res.data]
                }));
                setNewPremioText('');
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Premio agregado.' });
            } else {
                showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al crear premio.' });
            }
        }
    };

    const handlePremioEdit = (premio) => {
        setEditingPremioId(premio.id || premio.Id);
        setNewPremioText(premio.texto);
    };

    const handlePremioDelete = async (id) => {
        setConfirmConfig({
            message: '¿Estás seguro de que deseas eliminar este premio?',
            textYes: 'Sí, eliminar',
            textNo: 'Cancelar',
            action: async () => {
                setIsLoading(true);
                const res = await DeletePremio(id);
                setIsLoading(false);
                if (res.success) {
                    setPremiosConfig(prev => {
                        const list = prev.premios.filter(p => p.id !== id && p.Id !== id);
                        const isActiveDeleted = (prev.activePremio?.id || prev.activePremio?.Id) === id;
                        return {
                            ...prev,
                            premios: list,
                            activePremio: isActiveDeleted ? null : prev.activePremio
                        };
                    });
                    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Premio eliminado.' });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al eliminar.' });
                    return { success: false };
                }
            }
        });
        setShowConfirm(true);
    };

    const handlePremioLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                setPremiosConfig(prev => ({ ...prev, logoDataUrl: evt.target.result }));
                showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Imagen cargada en previsualización local.' });
            };
            reader.readAsDataURL(file);
        }
    };

    const handlePremioLogoDelete = () => {
        setPremiosConfig(prev => ({ ...prev, logoDataUrl: null }));
        if (premiosLogoRef.current) premiosLogoRef.current.value = '';
        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Imagen superior removida.' });
    };

    const handlePremioMoveUp = async (index) => {
        if (index === 0) return;
        const list = [...premiosConfig.premios];
        // Swap positions
        const temp = list[index];
        list[index] = list[index - 1];
        list[index - 1] = temp;

        // Reassign position values
        list.forEach((p, idx) => { p.position = idx; p.Position = idx; });

        setPremiosConfig(prev => ({ ...prev, premios: list }));
        // Save swaps to database
        setIsLoading(true);
        await UpdatePremio(list[index].id || list[index].Id, list[index]);
        await UpdatePremio(list[index - 1].id || list[index - 1].Id, list[index - 1]);
        setIsLoading(false);
    };

    const handlePremioMoveDown = async (index) => {
        const list = [...premiosConfig.premios];
        if (index === list.length - 1) return;
        // Swap positions
        const temp = list[index];
        list[index] = list[index + 1];
        list[index + 1] = temp;

        // Reassign position values
        list.forEach((p, idx) => { p.position = idx; p.Position = idx; });

        setPremiosConfig(prev => ({ ...prev, premios: list }));
        // Save swaps to database
        setIsLoading(true);
        await UpdatePremio(list[index].id || list[index].Id, list[index]);
        await UpdatePremio(list[index + 1].id || list[index + 1].Id, list[index + 1]);
        setIsLoading(false);
    };

    const handlePremiosPrev = async () => {
        const list = premiosConfig.premios;
        if (list.length === 0) return;
        const currentActive = list.find(p => p.isActive);
        let nextIdx = list.length - 1;
        if (currentActive) {
            const currentIdx = list.indexOf(currentActive);
            nextIdx = currentIdx === 0 ? list.length - 1 : currentIdx - 1;
        }
        await handlePremioSelect(list[nextIdx].id || list[nextIdx].Id, false);
    };

    const handlePremiosNext = async () => {
        const list = premiosConfig.premios;
        if (list.length === 0) return;
        const currentActive = list.find(p => p.isActive);
        let nextIdx = 0;
        if (currentActive) {
            const currentIdx = list.indexOf(currentActive);
            nextIdx = (currentIdx + 1) % list.length;
        }
        await handlePremioSelect(list[nextIdx].id || list[nextIdx].Id, false);
    };

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={setConfirmData}
                />
            )}

            <div className="flex flex-col gap-6 p-4 w-full max-w-6xl mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>
                
                {/* 
                ==========================================================================
                   SECCIÓN SUPERIOR: ACCIONES RÁPIDAS Y REPRODUCTOR CUMPLEANOS
                ========================================================================== */}
                <div className="mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>
                    {/* Acciones Rápidas */}
                    <div className="mb-4">
                        <h4 className="text-white text-md fw-bold mb-3">Acciones rápidas</h4>
                        <div 
                            className="d-flex flex-nowrap gap-3 overflow-x-auto pb-3"
                            style={{
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#ff003c rgba(255,255,255,0.05)'
                            }}
                        >
                            {/* 1. En vivo */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showLiveBadgeList ? '#ff003c' : '#ffffff',
                                    color: showLiveBadgeList ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showLiveBadgeList ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showLiveBadgeList;
                                    setShowLiveBadgeList(nextState);
                                    if (!nextState) {
                                        await HideTombolaOverlay();
                                        setConfig(prev => ({ ...prev, visible: false }));
                                        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Insignia oculta.' });
                                    }
                                }}
                            >
                                <div style={{
                                    width: '28px',
                                    height: '28px',
                                    borderRadius: '50%',
                                    backgroundColor: showLiveBadgeList ? '#ffffff' : '#ff003c',
                                    marginBottom: '10px'
                                }} />
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showLiveBadgeList ? '✓ Cerrar' : 'Abrir'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    En vivo
                                </span>
                            </button>

                            {/* 1b. Logotipo Overlay */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: logoConfig.visible ? '#ff003c' : '#ffffff',
                                    color: logoConfig.visible ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: logoConfig.visible ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    await triggerToggleLogoVisibility(!logoConfig.visible);
                                }}
                            >
                                <i className="bi bi-image" style={{
                                    fontSize: '28px',
                                    color: logoConfig.visible ? '#ffffff' : '#10b981',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {logoConfig.visible ? '✓ Ocultar' : 'Mostrar'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Logotipo
                                </span>
                            </button>

                            {/* 2. Contacto */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showContactoList ? '#ff003c' : '#ffffff',
                                    color: showContactoList ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showContactoList ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showContactoList;
                                    setShowContactoList(nextState);
                                    if (!nextState) {
                                        await ToggleContactoVisibility(false);
                                        setContactConfig(prev => ({ ...prev, visible: false }));
                                        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Contacto oculto de pantalla.' });
                                    }
                                }}
                            >
                                <i className="bi bi-person-lines-fill" style={{
                                    fontSize: '28px',
                                    color: showContactoList ? '#ffffff' : '#0ea5e9',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showContactoList ? '✓ Cerrar' : 'Abrir'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Contacto
                                </span>
                            </button>

                            {/* 3. Portada */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: portadaConfig.visible ? '#ff003c' : '#ffffff',
                                    color: portadaConfig.visible ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: portadaConfig.visible ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={() => triggerTogglePortadaVisibility(!portadaConfig.visible)}
                            >
                                <i className="bi bi-disc" style={{
                                    fontSize: '28px',
                                    color: portadaConfig.visible ? '#ffffff' : '#ff003c',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {portadaConfig.visible ? '✓ Ocultar' : 'Mostrar'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Portada
                                </span>
                            </button>

                            {/* 4. Detalles técnicos */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: soporteConfig.visible ? '#ff003c' : '#ffffff',
                                    color: soporteConfig.visible ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: soporteConfig.visible ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={() => triggerToggleSoporteVisibility(!soporteConfig.visible)}
                            >
                                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: '6px' }}>
                                    <path d="M12 2L1 21H23L12 2Z" fill={soporteConfig.visible ? "#ffffff" : "#ffb700"} />
                                    <path d="M12 9V14" stroke={soporteConfig.visible ? "#ff003c" : "#000000"} strokeWidth="2.5" strokeLinecap="round" />
                                    <circle cx="12" cy="17" r="1.5" fill={soporteConfig.visible ? "#ff003c" : "#000000"} />
                                </svg>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {soporteConfig.visible ? '✓ Ocultar' : 'Mostrar'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Msg. Técnico
                                </span>
                            </button>

                            {/* 5. Cumpleañero */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showCumpleanosPlayer ? '#ff003c' : '#ffffff',
                                    color: showCumpleanosPlayer ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showCumpleanosPlayer ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showCumpleanosPlayer;
                                    setShowCumpleanosPlayer(nextState);
                                    if (!nextState) {
                                        await HideCumpleanosOverlay();
                                        setCumpleanosConfig(prev => ({ ...prev, visible: false }));
                                    }
                                }}
                            >
                                <i className="bi bi-cake2-fill" style={{
                                    fontSize: '28px',
                                    color: showCumpleanosPlayer ? '#ffffff' : '#ffb700',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showCumpleanosPlayer ? 'Ocultar' : 'Mostrar'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    pantalla
                                </span>
                            </button>

                            {/* 6. Menciones */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showMencionesList ? '#ff003c' : '#ffffff',
                                    color: showMencionesList ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showMencionesList ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showMencionesList;
                                    setShowMencionesList(nextState);
                                    if (!nextState) {
                                        await ToggleMencionesVisibility(false);
                                        setMencionesConfig(prev => ({ ...prev, visible: false }));
                                    }
                                }}
                            >
                                <i className="bi bi-megaphone-fill" style={{
                                    fontSize: '28px',
                                    color: showMencionesList ? '#ffffff' : '#9333ea',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showMencionesList ? '✓ Cerrar' : 'Abrir'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Menciones
                                </span>
                            </button>

                            {/* 7. Preguntas */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showPreguntasList ? '#ff003c' : '#ffffff',
                                    color: showPreguntasList ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showPreguntasList ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showPreguntasList;
                                    setShowPreguntasList(nextState);
                                    if (!nextState) {
                                        await TogglePreguntasVisibility(false);
                                        setPreguntasConfig(prev => ({ ...prev, visible: false }));
                                        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Preguntas ocultas de pantalla.' });
                                    }
                                }}
                            >
                                <i className="bi bi-question-circle-fill" style={{
                                    fontSize: '28px',
                                    color: showPreguntasList ? '#ffffff' : '#3b82f6',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showPreguntasList ? '✓ Cerrar' : 'Abrir'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Preguntas
                                </span>
                            </button>

                            {/* 8. Premios */}
                            <button
                                type="button"
                                className="d-flex flex-column align-items-center justify-content-center p-3 rounded-4 border-0"
                                style={{
                                    width: '150px',
                                    height: '120px',
                                    flexShrink: 0,
                                    background: showPremiosList ? '#ff003c' : '#ffffff',
                                    color: showPremiosList ? '#ffffff' : '#000000',
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                                    transition: 'all 0.2s',
                                    border: showPremiosList ? 'none' : '1px solid #ff003c'
                                }}
                                onClick={async () => {
                                    const nextState = !showPremiosList;
                                    setShowPremiosList(nextState);
                                    if (!nextState) {
                                        await TogglePremiosVisibility(false);
                                        setPremiosConfig(prev => ({ ...prev, visible: false }));
                                        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Premios ocultas de pantalla.' });
                                    }
                                }}
                            >
                                <i className="bi bi-gift-fill" style={{
                                    fontSize: '28px',
                                    color: showPremiosList ? '#ffffff' : '#f59e0b',
                                    marginBottom: '6px'
                                }}></i>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    {showPremiosList ? '✓ Cerrar' : 'Abrir'}
                                </span>
                                <span className="text-xs fw-bold" style={{ textAlign: 'center' }}>
                                    Premios
                                </span>
                            </button>
                        </div>
                    </div>

                    {/* Tarjeta Reproductor Cumpleañeros */}
                    {showCumpleanosPlayer && (
                        <div className="p-4 rounded-4 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            {/* Tache de Cierre Absoluto */}
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onClick={async () => {
                                    setShowCumpleanosPlayer(false);
                                    await HideCumpleanosOverlay();
                                    setCumpleanosConfig(prev => ({ ...prev, visible: false }));
                                }}
                                title="Cerrar reproductor"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {/* Título Principal */}
                            <div className="mb-3">
                                <span className="fw-bold fs-5 text-dark">Cumpleañeros</span>
                            </div>

                            {/* Contenedor de Dos Columnas */}
                            <div className="row g-4">
                                {/* Columna Izquierda: Deck / Controles */}
                                <div className="col-lg-7 col-md-12">
                                    <div className="p-3 rounded-3" style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                        {/* Controles superiores del deck (Intervalo y Toggles de pantalla) */}
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <div className="d-flex align-items-center gap-2">
                                                <small className="text-muted fw-bold">Intervalo:</small>
                                                <select 
                                                    className="form-select form-select-sm w-auto bg-white text-dark border-secondary"
                                                    value={cumpleanosConfig.rotationSpeed}
                                                    onChange={(e) => updateCumpleanosConfigAndBroadcast({ rotationSpeed: Number(e.target.value) })}
                                                >
                                                    <option value={3}>3s</option>
                                                    <option value={5}>5s</option>
                                                    <option value={8}>8s</option>
                                                    <option value={10}>10s</option>
                                                    <option value={15}>15s</option>
                                                    <option value={20}>20s</option>
                                                </select>
                                            </div>

                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className={`btn btn-sm rounded-pill px-3 fw-semibold transition-all ${
                                                        cumpleanosConfig.showConfetti ? 'btn-danger text-white border-0' : 'btn-outline-danger'
                                                    }`}
                                                    style={{ fontSize: '0.75rem', padding: '4px 12px' }}
                                                    onClick={() => updateCumpleanosConfigAndBroadcast({ showConfetti: !cumpleanosConfig.showConfetti })}
                                                >
                                                    {cumpleanosConfig.showConfetti ? 'Ocultar confeti' : 'Mostrar confeti'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Lienzo / Screen de visualización del cumpleañero activo */}
                                        <div className="d-flex align-items-center justify-content-center text-center rounded-3 p-4 mb-3" style={{
                                            height: '180px',
                                            background: 'radial-gradient(circle, #f8f9fa 0%, #e9ecef 100%)',
                                            border: '1.5px dashed rgba(0,0,0,0.1)'
                                        }}>
                                            {getBirthdayList().length > 0 ? (
                                                <div className="w-100">
                                                    <span className="text-muted text-xs d-block mb-1 text-uppercase fw-extrabold text-danger" style={{ letterSpacing: '2px' }}>
                                                        Felicidades
                                                    </span>
                                                    <span className="fw-extrabold text-dark d-block fs-2 my-2" style={{ wordBreak: 'break-word', letterSpacing: '-0.5px' }}>
                                                        {cumpleanosConfig.activeName || 'Ninguno'}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-muted fs-5 italic">Fila vacía</span>
                                            )}
                                        </div>

                                        {/* Botones de navegación inferiores planos */}
                                        <div className="d-flex justify-content-center gap-2">
                                            <button 
                                                type="button" 
                                                className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                                                style={{ width: '60px', height: '40px', borderWidth: '1.5px' }}
                                                onClick={handlePlayerPrev}
                                            >
                                                <i className="bi bi-skip-start-fill fs-5"></i>
                                            </button>
                                            <button 
                                                type="button" 
                                                className={`btn d-flex align-items-center justify-content-center ${cumpleanosConfig.isPlaying ? 'btn-danger text-white border-0' : 'btn-outline-danger'}`}
                                                style={{ width: '80px', height: '40px', borderWidth: '1.5px' }}
                                                onClick={cumpleanosConfig.isPlaying ? handlePlayerPause : handlePlayerPlay}
                                            >
                                                <i className={`bi bi-${cumpleanosConfig.isPlaying ? 'pause-fill' : 'play-fill'} fs-4`}></i>
                                            </button>
                                            <button 
                                                type="button" 
                                                className="btn btn-outline-danger d-flex align-items-center justify-content-center"
                                                style={{ width: '60px', height: '40px', borderWidth: '1.5px' }}
                                                onClick={handlePlayerNext}
                                            >
                                                <i className="bi bi-skip-end-fill fs-5"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna Derecha: Fila lateral */}
                                <div className="col-lg-5 col-md-12">
                                    <div className="p-3 rounded-3 h-100 d-flex flex-column" style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.05)', minHeight: '280px' }}>
                                        <div className="d-flex justify-content-between align-items-center mb-2 pb-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                                            <span className="fw-bold text-dark text-sm">Fila ({getBirthdayList().length})</span>
                                            <button 
                                                type="button" 
                                                className="btn btn-link btn-sm text-danger text-decoration-none p-0 fw-bold"
                                                onClick={() => {
                                                    setConfirmConfig({
                                                        message: '¿Estás seguro de que deseas eliminar todos los cumpleañeros de la lista?',
                                                        textYes: 'Sí, eliminar todo',
                                                        textNo: 'Cancelar',
                                                        action: async () => {
                                                            updateBirthdayList([]);
                                                            return { success: true };
                                                        }
                                                    });
                                                    setShowConfirm(true);
                                                }}
                                                disabled={getBirthdayList().length === 0}
                                            >
                                                Eliminar todo
                                            </button>
                                        </div>

                                        <div className="overflow-y-auto flex-grow-1" style={{ maxHeight: '185px', fontSize: '0.85rem' }}>
                                            {getBirthdayList().length === 0 ? (
                                                <p className="text-center text-muted small mt-4 italic">No hay cumpleañeros en la fila</p>
                                            ) : (
                                                getBirthdayList().map((name, index) => {
                                                    const isSelected = name === cumpleanosConfig.activeName;
                                                    const hasBeenShown = shownBirthdays.includes(name);
                                                    return (
                                                        <div 
                                                            key={index} 
                                                            className={`d-flex align-items-center justify-content-between p-2 mb-1.5 rounded transition-all cursor-pointer ${
                                                                isSelected 
                                                                ? 'bg-danger text-white' 
                                                                : 'bg-white text-dark'
                                                            }`}
                                                            style={{ 
                                                                border: isSelected 
                                                                    ? '2px solid #ff003c' 
                                                                    : (hasBeenShown ? '2.5px solid #22c55e' : '1.5px solid rgba(0,0,0,0.06)')
                                                            }}
                                                            onClick={() => handleSelectAndPlayBirthday(name)}
                                                        >
                                                            <div className="d-flex align-items-center gap-2">
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs p-0 border-0 ${isSelected ? 'text-white' : 'text-danger'}`}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDeleteBirthday(index);
                                                                    }}
                                                                    title="Quitar"
                                                                >
                                                                    <i className="bi bi-trash-fill" style={{ fontSize: '0.85rem' }}></i>
                                                                </button>
                                                                <span className="fw-semibold text-truncate" style={{ maxWidth: '120px' }}>{name}</span>
                                                                {hasBeenShown && !isSelected && (
                                                                    <i className="bi bi-check-circle-fill text-success ms-1.5" style={{ fontSize: '0.95rem' }} title="Ya mostrado"></i>
                                                                )}
                                                            </div>

                                                            <div className="d-flex align-items-center gap-1">
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs p-0 border-0 ${isSelected ? 'text-white' : 'text-secondary'}`}
                                                                    onClick={(e) => { e.stopPropagation(); handleMoveBirthday(index, 'up'); }}
                                                                    disabled={index === 0}
                                                                >
                                                                    <i className="bi bi-chevron-up"></i>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs p-0 border-0 ${isSelected ? 'text-white' : 'text-secondary'}`}
                                                                    onClick={(e) => { e.stopPropagation(); handleMoveBirthday(index, 'down'); }}
                                                                    disabled={index === getBirthdayList().length - 1}
                                                                >
                                                                    <i className="bi bi-chevron-down"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                    {/* Tarjeta Acciones Rápidas En Vivo (Insignia) */}
                    {showLiveBadgeList && (
                        <div className="p-4 rounded-4 mt-3 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                }}
                                onClick={async () => {
                                    setShowLiveBadgeList(false);
                                    await HideTombolaOverlay();
                                    setConfig(prev => ({ ...prev, visible: false }));
                                    showMessage({ type: MESSAGE_TYPE.INFO, text: 'Insignia oculta.' });
                                }}
                                title="Cerrar panel de insignias"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="fw-bold fs-5 text-dark">Insignias en Vivo</span>
                            </div>

                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {(!config.liveBadges || config.liveBadges.length === 0) ? (
                                    <span className="text-muted text-xs text-center py-3">No hay insignias registradas.</span>
                                ) : (
                                    config.liveBadges.map((b) => {
                                        const isAct = b.isActive && config.visible;
                                        return (
                                            <div key={b.id || b.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    {b.logoDataUrl ? (
                                                        <img src={b.logoDataUrl} alt="Logo" style={{ width: '32px', height: '32px', objectFit: 'contain', background: '#fff', border: '1px solid rgba(0,0,0,0.1)' }} />
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0,0,0,0.05)', color: '#000000', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            LV
                                                        </div>
                                                    )}
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-bold text-sm text-dark">{b.text}</span>
                                                    </div>
                                                </div>
                                                <div className="d-flex gap-2">
                                                    {/* Toggle Icono */}
                                                    <button
                                                        type="button"
                                                        className={`btn btn-xs py-1 px-2.5 rounded-2 ${b.showLogo ? 'btn-success text-white border-0' : 'btn-outline-secondary'}`}
                                                        onClick={() => handleToggleLiveBadgeLogo(b.id || b.Id)}
                                                        title="Alternar logotipo"
                                                    >
                                                        <i className={`bi ${b.showLogo ? 'bi-image-fill' : 'bi-image'}`}></i>
                                                    </button>
                                                    {/* Activar/Proyectar */}
                                                    <button
                                                        type="button"
                                                        className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs ${
                                                            isAct ? 'btn-danger text-white border-0' : 'btn-outline-secondary'
                                                        }`}
                                                        onClick={() => handleLiveBadgeSelect(b.id || b.Id, b.isActive)}
                                                    >
                                                        {isAct ? '✓ Ocultar' : 'Mostrar'}
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}



                    {/* Tarjeta Acciones Rápidas Contacto */}
                    {showContactoList && (
                        <div className="p-4 rounded-4 mt-3 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            {/* Tache de Cierre Absoluto */}
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onClick={async () => {
                                    setShowContactoList(false);
                                    await ToggleContactoVisibility(false);
                                    setContactConfig(prev => ({ ...prev, visible: false }));
                                    showMessage({ type: MESSAGE_TYPE.INFO, text: 'Contacto oculto de pantalla.' });
                                }}
                                title="Cerrar panel de contacto"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="fw-bold fs-5 text-dark">Canales de Contacto</span>
                            </div>

                            {/* Listado de canales de contacto rápidos */}
                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {(!contactConfig.contactos || contactConfig.contactos.length === 0) ? (
                                    <span className="text-muted text-xs text-center py-3">No hay canales de contacto registrados.</span>
                                ) : (
                                    contactConfig.contactos.map((c) => {
                                        const isAct = (c.isActive || c.IsActive) && contactConfig.visible;
                                        return (
                                            <div key={c.id || c.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                                <div className="d-flex align-items-center gap-2">
                                                    {c.iconDataUrl ? (
                                                        <img src={c.iconDataUrl} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'contain', background: '#fff', border: '1px solid rgba(0,0,0,0.1)' }} />
                                                    ) : (
                                                        <div className="d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%', background: c.iconBgColor || '#ff003c', color: c.iconSymbolColor || '#fff', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                            {c.nombreCanal.substring(0, 2).toUpperCase()}
                                                        </div>
                                                    )}
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-bold text-sm text-dark">{c.nombreCanal}</span>
                                                        <small className="text-muted text-xs">{c.phoneNumber}</small>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs d-flex align-items-center gap-1 transition-all ${
                                                        isAct ? 'btn-danger text-white border-0' : 'btn-outline-secondary'
                                                    }`}
                                                    style={{
                                                        fontSize: '0.7rem',
                                                        backgroundColor: isAct ? '#ff003c' : 'transparent',
                                                        borderColor: isAct ? 'transparent' : 'rgba(0,0,0,0.15)',
                                                        color: isAct ? '#ffffff' : '#000000'
                                                    }}
                                                    onClick={() => handleContactoSelect(c.id || c.Id, c.isActive || c.IsActive)}
                                                >
                                                    {isAct ? (
                                                        <>
                                                            <i className="bi bi-check-circle-fill"></i> ✓ Ocultar
                                                        </>
                                                    ) : (
                                                        'Mostrar'
                                                    )}
                                                </button>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tarjeta Acciones Rápidas Menciones */}
                    {showMencionesList && (
                        <div className="p-4 rounded-4 mt-3 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            {/* Tache de Cierre Absoluto */}
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onClick={async () => {
                                    setShowMencionesList(false);
                                    await ToggleMencionesVisibility(false);
                                    setMencionesConfig(prev => ({ ...prev, visible: false }));
                                }}
                                title="Cerrar panel de menciones"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="fw-bold fs-5 text-dark">Menciones</span>
                            </div>

                            {/* Listado de menciones rápidas */}
                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {mencionesConfig.menciones.length === 0 ? (
                                    <span className="text-muted text-xs text-center py-3">No hay menciones registradas.</span>
                                ) : (
                                    mencionesConfig.menciones.map((m) => (
                                        <div key={m.id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <div className="d-flex align-items-center gap-2">
                                                {m.logoDataUrl ? (
                                                    <img src={m.logoDataUrl} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'contain', background: '#fff', border: '1px solid rgba(0,0,0,0.1)' }} />
                                                ) : (
                                                    <div className="d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#ff003c', color: '#fff', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                        {m.nombreInterno.substring(0, 2).toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="fw-bold text-sm text-dark">{m.nombreInterno}</span>
                                            </div>
                                            <button
                                                type="button"
                                                className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs d-flex align-items-center gap-1 transition-all ${
                                                    m.isActive && mencionesConfig.visible
                                                    ? 'btn-danger text-white border-0' 
                                                    : 'btn-outline-secondary'
                                                }`}
                                                style={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: (m.isActive && mencionesConfig.visible) ? '#ff003c' : 'transparent',
                                                    borderColor: (m.isActive && mencionesConfig.visible) ? 'transparent' : 'rgba(0,0,0,0.15)',
                                                    color: (m.isActive && mencionesConfig.visible) ? '#ffffff' : '#000000'
                                                }}
                                                onClick={() => handleMencionToggleSelect(m.id, m.isActive)}
                                            >
                                                {(m.isActive && mencionesConfig.visible) ? (
                                                    <>
                                                        <i className="bi bi-check-circle-fill"></i> ✓ Ocultar
                                                    </>
                                                ) : (
                                                    'Mostrar'
                                                )}
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tarjeta Acciones Rápidas Preguntas */}
                    {showPreguntasList && (
                        <div className="p-4 rounded-4 mt-3 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            {/* Tache de Cierre Absoluto */}
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onClick={async () => {
                                    setShowPreguntasList(false);
                                    await TogglePreguntasVisibility(false);
                                    setPreguntasConfig(prev => ({ ...prev, visible: false }));
                                }}
                                title="Cerrar panel de preguntas"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="fw-bold fs-5 text-dark">Preguntas</span>
                            </div>

                            {/* Listado de preguntas rápidas */}
                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {preguntasConfig.preguntas.length === 0 ? (
                                    <span className="text-muted text-xs text-center py-3">No hay preguntas registradas.</span>
                                ) : (
                                    preguntasConfig.preguntas.map((p) => (
                                        <div key={p.id || p.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <span className="fw-bold text-sm text-dark">{p.texto}</span>
                                            <button
                                                type="button"
                                                className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs d-flex align-items-center gap-1 transition-all ${
                                                    p.isActive && preguntasConfig.visible
                                                    ? 'btn-danger text-white border-0' 
                                                    : 'btn-outline-secondary'
                                                }`}
                                                style={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: (p.isActive && preguntasConfig.visible) ? '#ff003c' : 'transparent',
                                                    borderColor: (p.isActive && preguntasConfig.visible) ? 'transparent' : 'rgba(0,0,0,0.15)',
                                                    color: (p.isActive && preguntasConfig.visible) ? '#ffffff' : '#000000'
                                                }}
                                                onClick={() => handlePreguntaSelect(p.id || p.Id, p.isActive)}
                                            >
                                                {(p.isActive && preguntasConfig.visible) ? (
                                                    <>
                                                        <i className="bi bi-check-circle-fill"></i> ✓ Ocultar
                                                    </>
                                                ) : (
                                                    'Mostrar'
                                                )}
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tarjeta Acciones Rápidas Premios */}
                    {showPremiosList && (
                        <div className="p-4 rounded-4 mt-3 position-relative" style={{
                            background: '#ffffff',
                            border: '2.5px solid #ff003c',
                            boxShadow: '0 8px 24px rgba(255, 0, 60, 0.15)',
                            color: '#000000',
                            fontFamily: "'Montserrat', sans-serif"
                        }}>
                            {/* Tache de Cierre Absoluto */}
                            <button
                                type="button"
                                className="btn p-0 border-0"
                                style={{
                                    position: 'absolute',
                                    top: '12px',
                                    right: '18px',
                                    background: 'transparent',
                                    fontSize: '1.25rem',
                                    color: '#718096',
                                    cursor: 'pointer',
                                    transition: 'color 0.2s'
                                }}
                                onClick={async () => {
                                    setShowPremiosList(false);
                                    await TogglePremiosVisibility(false);
                                    setPremiosConfig(prev => ({ ...prev, visible: false }));
                                }}
                                title="Cerrar panel de premios"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>

                            {/* Header */}
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <span className="fw-bold fs-5 text-dark">Premios</span>
                            </div>

                            {/* Listado de premios rápidos */}
                            <div className="d-flex flex-column gap-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {premiosConfig.premios.length === 0 ? (
                                    <span className="text-muted text-xs text-center py-3">No hay premios registrados.</span>
                                ) : (
                                    premiosConfig.premios.map((p) => (
                                        <div key={p.id || p.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <span className="fw-bold text-sm text-dark">{p.texto}</span>
                                            <button
                                                type="button"
                                                className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs d-flex align-items-center gap-1 transition-all ${
                                                    p.isActive && premiosConfig.visible
                                                    ? 'btn-danger text-white border-0' 
                                                    : 'btn-outline-secondary'
                                                }`}
                                                style={{
                                                    fontSize: '0.7rem',
                                                    backgroundColor: (p.isActive && premiosConfig.visible) ? '#ff003c' : 'transparent',
                                                    borderColor: (p.isActive && premiosConfig.visible) ? 'transparent' : 'rgba(0,0,0,0.15)',
                                                    color: (p.isActive && premiosConfig.visible) ? '#ffffff' : '#000000'
                                                }}
                                                onClick={() => handlePremioSelect(p.id || p.Id, p.isActive)}
                                            >
                                                {(p.isActive && premiosConfig.visible) ? (
                                                    <>
                                                        <i className="bi bi-check-circle-fill"></i> ✓ Ocultar
                                                    </>
                                                ) : (
                                                    'Mostrar'
                                                )}
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 1: PANTALLA DE EN VIVO (INSIGNIA)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsExpanded(!isExpanded); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); setIsLogoExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-projector-fill me-1"></i>
                            <span>Pantalla de En Vivo (Insignia)</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: config.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: config.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${config.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {config.visible ? '🟢 La insignia se está mostrando en pantalla' : '🔴 La insignia está oculta'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    config.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: config.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerToggleOverlayVisibility(!config.visible);
                                }}
                            >
                                {config.visible ? 'Ocultar insignia' : 'Mostrar insignia'}
                            </button>
                            <i className={`bi fs-5 ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Insignia Activa: <strong className="text-white">"{config.activeLiveBadge?.text || 'Ninguna'}"</strong> | 
                                Estilo: <strong className="text-white">{config.designStyle === 'logo' ? 'Con Logo' : 'Simple'}</strong> | 
                                Zoom: <strong className="text-white">{config.scale.toFixed(2)}x</strong> | 
                                Posición: <strong className="text-white">{config.position}</strong> (X: {config.offsetX}%, Y: {config.offsetY}%)
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsExpanded(true); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); setIsLogoExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {/* Cuerpo de Ajustes Expandido */}
                    {isExpanded && (
                        <div className="card-body p-4" style={{ background: '#0e111a' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: CRUD de insignias registradas */}
                                <div className="col-lg-7 col-md-12 d-flex flex-column gap-3">
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📬 {editingLiveBadgeItemId !== -1 ? 'Editar Insignia' : 'Agregar Insignia'}
                                        </h3>

                                        <div className="row g-3">
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Texto de la Insignia:</label>
                                                <input 
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={newLiveBadgeItem.text}
                                                    onChange={(e) => setNewLiveBadgeItem(prev => ({ ...prev, text: e.target.value }))}
                                                    placeholder="Ej: AL AIRE, EN VIVO, PROGRAMA ESPECIAL"
                                                />
                                            </div>
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs">Subir Logotipo Opcional:</label>
                                                <div className="d-flex gap-2">
                                                    <input 
                                                        type="file"
                                                        ref={liveBadgeLogoRef}
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={handleLiveBadgeItemLogoUpload}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-light flex-fill py-2 rounded-2 text-xs"
                                                        onClick={() => liveBadgeLogoRef.current.click()}
                                                    >
                                                        Subir Logo
                                                    </button>
                                                    {newLiveBadgeItem.logoDataUrl && (
                                                        <button 
                                                            type="button"
                                                            className="btn btn-danger px-3.5 py-1 text-xs rounded-2"
                                                            onClick={handleLiveBadgeItemLogoReset}
                                                        >
                                                            ✖
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex align-items-center gap-2 mt-1">
                                            <input 
                                                type="checkbox"
                                                id="showBadgeLogoCheckbox"
                                                className="form-check-input"
                                                checked={newLiveBadgeItem.showLogo}
                                                onChange={(e) => setNewLiveBadgeItem(prev => ({ ...prev, showLogo: e.target.checked }))}
                                            />
                                            <label htmlFor="showBadgeLogoCheckbox" className="text-white text-xs cursor-pointer select-none mb-0">
                                                Mostrar logo / ícono en la insignia
                                            </label>
                                        </div>

                                        <div className="d-flex justify-content-end gap-2 mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                            {editingLiveBadgeItemId !== -1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary py-2 px-4 text-xs font-semibold rounded-2 text-white"
                                                    onClick={() => {
                                                        setEditingLiveBadgeItemId(-1);
                                                        setNewLiveBadgeItem({ text: '', logoDataUrl: null, showLogo: true });
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-danger py-2 px-4 text-xs font-semibold rounded-2 text-white border-0"
                                                onClick={handleLiveBadgeAdd}
                                            >
                                                {editingLiveBadgeItemId !== -1 ? 'Actualizar Insignia' : 'Guardar Insignia'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Listado */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📋 Insignias Registradas ({config.liveBadges?.length || 0})
                                        </h3>

                                        <div className="d-flex flex-column gap-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                            {(!config.liveBadges || config.liveBadges.length === 0) ? (
                                                <span className="text-muted text-xs text-center py-4">No hay insignias registradas. Agrega una arriba.</span>
                                            ) : (
                                                config.liveBadges.map((b, index) => {
                                                    const isAct = b.isActive && config.visible;
                                                    return (
                                                        <div key={b.id || b.Id} className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                                                                    {b.logoDataUrl ? (
                                                                        <img src={b.logoDataUrl} alt="Logo" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                                                                    ) : (
                                                                        <i className="bi bi-projector text-muted fs-5"></i>
                                                                    )}
                                                                </div>
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-bold text-sm text-white">{b.text}</span>
                                                                    <small className="text-muted text-xs">
                                                                        Icono: <span className={b.showLogo ? 'text-success' : 'text-danger'}>{b.showLogo ? 'Visible' : 'Oculto'}</span>
                                                                    </small>
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2">
                                                                {/* Mostrar / Ocultar en Vivo */}
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs ${
                                                                        isAct ? 'btn-danger text-white border-0' : 'btn-outline-secondary'
                                                                    }`}
                                                                    style={{ minWidth: '85px' }}
                                                                    onClick={() => handleLiveBadgeSelect(b.id || b.Id, b.isActive)}
                                                                >
                                                                    {isAct ? '✓ Ocultar' : 'Mostrar'}
                                                                </button>

                                                                {/* Toggle logo */}
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs py-1 px-2.5 rounded-2 border-0 ${b.showLogo ? 'btn-outline-success' : 'btn-outline-secondary'}`}
                                                                    onClick={() => handleToggleLiveBadgeLogo(b.id || b.Id)}
                                                                    title="Alternar visibilidad del logotipo"
                                                                >
                                                                    <i className={`bi ${b.showLogo ? 'bi-image-fill' : 'bi-image'}`}></i>
                                                                </button>

                                                                {/* Reordenamiento */}
                                                                <div className="d-flex flex-column gap-0.5">
                                                                    <button type="button" className="btn btn-outline-light btn-xs p-0 px-1 border-0" disabled={index === 0} onClick={() => handleLiveBadgeMove(index, 'up')}>
                                                                        <i className="bi bi-caret-up-fill"></i>
                                                                    </button>
                                                                    <button type="button" className="btn btn-outline-light btn-xs p-0 px-1 border-0" disabled={index === config.liveBadges.length - 1} onClick={() => handleLiveBadgeMove(index, 'down')}>
                                                                        <i className="bi bi-caret-down-fill"></i>
                                                                    </button>
                                                                </div>

                                                                {/* Editar */}
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-outline-light btn-xs py-1 px-2.5 rounded-2 border-0" 
                                                                    onClick={() => {
                                                                        setEditingLiveBadgeItemId(b.id || b.Id);
                                                                        setNewLiveBadgeItem({
                                                                            text: b.text,
                                                                            logoDataUrl: b.logoDataUrl,
                                                                            showLogo: b.showLogo
                                                                        });
                                                                    }}
                                                                >
                                                                    <i className="bi bi-pencil-fill text-info"></i>
                                                                </button>

                                                                {/* Eliminar */}
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-outline-light btn-xs py-1 px-2.5 rounded-2 border-0"
                                                                    onClick={() => handleLiveBadgeDelete(b.id || b.Id)}
                                                                >
                                                                    <i className="bi bi-trash3-fill text-danger"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Parámetros Visuales y Diseño */}
                                <div className="col-lg-5 col-md-12 d-flex flex-column gap-3">
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            🎨 Diseño y Estilo de Insignia
                                        </h3>

                                        <div className="tombola-input-item d-flex flex-column gap-2">
                                            <label className="text-muted text-xs fw-semibold">Estilo General:</label>
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className={`btn flex-fill py-2 rounded-2 fw-semibold text-sm ${config.designStyle === 'simple' ? 'btn-danger border-0' : 'btn-outline-secondary'}`}
                                                    onClick={() => handleStyleChange('simple')}
                                                >
                                                    Simple
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`btn flex-fill py-2 rounded-2 fw-semibold text-sm ${config.designStyle === 'logo' ? 'btn-danger border-0' : 'btn-outline-secondary'}`}
                                                    onClick={() => handleStyleChange('logo')}
                                                >
                                                    Con Logo
                                                </button>
                                            </div>
                                        </div>

                                        <div className="row g-2">
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Placa:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '35px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={config.color}
                                                    onChange={(e) => handleConfigChange('color', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-6">
                                                {renderSliderWithInput("Resplandor:", config.glow, 0, 30, 1, "px", (val) => handleConfigChange('glow', val))}
                                            </div>
                                        </div>

                                        <hr style={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                                        <div className="tombola-input-item d-flex flex-column gap-2">
                                            <label className="text-muted text-xs fw-semibold">Alineación de Posición:</label>
                                            <div className="row g-2">
                                                {['top-left', 'top-right', 'top-center', 'center'].map((pos) => (
                                                    <div key={pos} className="col-6">
                                                        <button
                                                            type="button"
                                                            className={`btn w-100 py-1.5 rounded-2 text-xs fw-bold ${config.position === pos ? 'btn-danger border-0 text-white' : 'btn-outline-secondary'}`}
                                                            onClick={() => handlePositionChange(pos)}
                                                        >
                                                            {pos === 'top-left' ? 'Arriba Izq.' : pos === 'top-right' ? 'Arriba Der.' : pos === 'top-center' ? 'Arriba Centro' : 'Centro Pantalla'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {renderSliderWithInput("Zoom Placa:", config.scale, 0.5, 6.0, 0.1, "x", (val) => handleConfigChange('scale', val))}
                                        {renderSliderWithInput("Velocidad Parpadeo:", config.speed, 0.3, 3.0, 0.1, "s", (val) => handleConfigChange('speed', val))}
                                        {renderSliderWithInput("Ajuste X:", config.offsetX, 0, 100, 1, "%", (val) => handleConfigChange('offsetX', val))}
                                        {renderSliderWithInput("Ajuste Y:", config.offsetY, 0, 100, 1, "%", (val) => handleConfigChange('offsetY', val))}

                                        {/* Comportamiento del Fondo */}
                                        <div className="tombola-input-item d-flex flex-column gap-2 mt-1">
                                            <label className="text-muted text-xs fw-semibold">Comportamiento del Fondo:</label>
                                            <button
                                                type="button"
                                                className={`btn w-100 py-2 rounded-2 d-flex align-items-center justify-content-center gap-2 fw-bold ${config.expanded ? 'btn-success border-0 text-white' : 'btn-outline-secondary'}`}
                                                onClick={() => setConfig(prev => ({ ...prev, expanded: !prev.expanded }))}
                                            >
                                                {config.expanded ? <><i className="bi bi-arrows-fullscreen"></i> Expandido (1920x1080)</> : <><i className="bi bi-layout-sidebar"></i> Columna Centrada</>}
                                            </button>
                                        </div>

                                        {/* Previsualización */}
                                        <div className="tombola-preview-box rounded-3 text-center my-2" style={{ height: '150px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                            <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                Previsualización
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                width: '1920px',
                                                height: '1080px',
                                                left: 'calc(50% - 160px)',
                                                top: '-20px',
                                                transform: 'scale(0.1666)',
                                                transformOrigin: 'top left',
                                                pointerEvents: 'none'
                                            }}>
                                                <TombolaOverlay config={{ ...config, visible: true }} />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn w-100 py-2.5 rounded-2 text-white fw-bold text-sm btn-save mt-2"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            disabled={isLoading}
                                            onClick={handleSaveConfig}
                                        >
                                            ⚡ Guardar Ajustes de Insignia
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA DE LOGOTIPO OVERLAY (NUEVO OVERLAY DE LOGO)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsLogoExpanded(!isLogoExpanded); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-image me-1"></i>
                            <span>Pantalla de Logotipo</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: logoConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: logoConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${logoConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {logoConfig.visible ? '🟢 El logotipo se está mostrando en pantalla' : '🔴 El logotipo está oculto'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    logoConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: logoConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerToggleLogoVisibility(!logoConfig.visible);
                                }}
                            >
                                {logoConfig.visible ? 'Ocultar Logotipo' : 'Mostrar Logotipo'}
                            </button>
                            <i className={`bi fs-5 ${isLogoExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isLogoExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Estado: <strong className="text-white">{logoConfig.logoDataUrl ? 'Logo Cargado' : 'Sin Imagen'}</strong> | 
                                Orientación: <strong className="text-white">{logoConfig.orientation}</strong> | 
                                Zoom: <strong className="text-white">{logoConfig.scale.toFixed(2)}x</strong> | 
                                Posición: <strong className="text-white">{logoConfig.position}</strong> (X: {logoConfig.offsetX}%, Y: {logoConfig.offsetY}%)
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsLogoExpanded(true); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {/* Cuerpo de Ajustes Expandido */}
                    {isLogoExpanded && (
                        <div className="card-body p-4" style={{ background: '#0e111a' }}>
                            <div className="row g-4">
                                
                                {/* Columna Izquierda: Imagen y Atributos */}
                                <div className="col-lg-6 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            🖼️ Carga de Logotipo
                                        </h3>

                                        <div className="tombola-input-item d-flex flex-column gap-2">
                                            <label className="text-muted text-xs fw-semibold">Imagen del Logotipo:</label>
                                            <div className="d-flex gap-2">
                                                <input 
                                                    type="file" 
                                                    ref={logoFileRef}
                                                    accept="image/*"
                                                    style={{ display: 'none' }}
                                                    onChange={handleLogoUpload}
                                                />
                                                <button 
                                                    type="button" 
                                                    className="btn btn-outline-light flex-fill py-2 rounded-2 text-sm fw-semibold"
                                                    onClick={() => logoFileRef.current.click()}
                                                >
                                                    Seleccionar Imagen
                                                </button>
                                                {logoConfig.logoDataUrl && (
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-danger px-3 py-2 rounded-2"
                                                        onClick={handleLogoReset}
                                                    >
                                                        ✖
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        <div className="tombola-input-item d-flex flex-column gap-1">
                                            <label className="text-muted text-xs fw-semibold">Orientación del Logo:</label>
                                            <select 
                                                className="form-select border-secondary text-white rounded-2"
                                                style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                value={logoConfig.orientation}
                                                onChange={(e) => handleLogoConfigChange('orientation', e.target.value)}
                                            >
                                                <option value="horizontal">Horizontal (Normal)</option>
                                                <option value="vertical">Vertical (Girar 90°)</option>
                                                <option value="rot-90">Rotación 90°</option>
                                                <option value="rot-180">Rotación 180°</option>
                                                <option value="rot-270">Rotación 270°</option>
                                                <option value="flip-h">Espejo Horizontal</option>
                                                <option value="flip-v">Espejo Vertical</option>
                                            </select>
                                        </div>

                                        {renderSliderWithInput("Zoom / Escala:", logoConfig.scale, 0.2, 5.0, 0.05, "x", (val) => handleLogoConfigChange('scale', val))}
                                    </div>
                                </div>

                                {/* Columna Derecha: Coordenadas y Previsualización */}
                                <div className="col-lg-6 col-md-12 d-flex flex-column gap-3">
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            ⚙️ Posición en Pantalla
                                        </h3>

                                        <div className="tombola-input-item d-flex flex-column gap-2">
                                            <label className="text-muted text-xs fw-semibold">Alineación de Posición:</label>
                                            <div className="row g-2">
                                                {['top-left', 'top-right', 'bottom-left', 'bottom-right', 'center'].map((pos) => (
                                                    <div key={pos} className="col-4">
                                                        <button
                                                            type="button"
                                                            className={`btn w-100 py-1.5 rounded-2 text-xs fw-bold ${logoConfig.position === pos ? 'btn-danger border-0 text-white' : 'btn-outline-secondary'}`}
                                                            onClick={() => handleLogoPositionChange(pos)}
                                                        >
                                                            {pos === 'top-left' ? 'Top-Izq' : pos === 'top-right' ? 'Top-Der' : pos === 'bottom-left' ? 'Bot-Izq' : pos === 'bottom-right' ? 'Bot-Der' : 'Centro'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {renderSliderWithInput("Desplazamiento X:", logoConfig.offsetX, 0, 100, 1, "%", (val) => handleLogoConfigChange('offsetX', val))}
                                        {renderSliderWithInput("Desplazamiento Y:", logoConfig.offsetY, 0, 100, 1, "%", (val) => handleLogoConfigChange('offsetY', val))}

                                        {/* Previsualización */}
                                        <div className="tombola-preview-box rounded-3 text-center my-2" style={{ height: '150px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                            <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                Previsualización Logo
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                width: '1920px',
                                                height: '1080px',
                                                left: 'calc(50% - 160px)',
                                                top: '-20px',
                                                transform: 'scale(0.1666)',
                                                transformOrigin: 'top left',
                                                pointerEvents: 'none'
                                            }}>
                                                <LogoOverlay config={{ ...logoConfig, visible: true }} />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn w-100 py-2.5 rounded-2 text-white fw-bold text-sm btn-save"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            disabled={isLoading}
                                            onClick={handleSaveLogoConfig}
                                        >
                                            ⚡ Guardar Ajustes de Logotipo
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 2: PANTALLA DE CONTACTO (WHATSAPP)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsContactExpanded(!isContactExpanded); setIsExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-person-lines-fill me-1"></i>
                            <span>Pantalla de Contacto</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: contactConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: contactConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${contactConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {contactConfig.visible ? '🟢 El banner de contacto se está mostrando en pantalla' : '🔴 El banner está oculto'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    contactConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: contactConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerToggleContactVisibility(!contactConfig.visible);
                                }}
                            >
                                {contactConfig.visible ? 'Ocultar banner' : 'Mostrar banner'}
                            </button>
                            <i className={`bi fs-5 ${isContactExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isContactExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Número: <strong className="text-white">"{contactConfig.phoneNumber}"</strong> | 
                                Fondo: <strong className="text-white">{contactConfig.bgType === 'color' ? 'Color Sólido' : contactConfig.bgType === 'image' ? 'Imagen' : 'Video'}</strong> | 
                                Zoom: <strong className="text-white">{contactConfig.scale.toFixed(2)}x</strong> | 
                                Posición: <strong className="text-white">{contactConfig.position}</strong> (X: {contactConfig.offsetX}%, Y: {contactConfig.offsetY}%)
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsContactExpanded(true); setIsExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {/* Cuerpo de Ajustes Expandido */}
                    {isContactExpanded && (
                        <div className="card-body p-4" style={{ background: '#0e111a' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Formulario y Canales de Contacto */}
                                <div className="col-lg-7 col-md-12 d-flex flex-column gap-3">
                                    
                                    {/* Formulario */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📬 {editingContactoItemId !== -1 ? 'Editar Canal de Contacto' : 'Agregar Canal de Contacto'}
                                        </h3>
                                        
                                        <div className="row g-3">
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Nombre del Canal:</label>
                                                <input 
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={newContactoItem.nombreCanal}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, nombreCanal: e.target.value }))}
                                                    placeholder="Ej: WhatsApp, Teléfono de Cabina"
                                                />
                                            </div>
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Número / Teléfono:</label>
                                                <input 
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={newContactoItem.phoneNumber}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, phoneNumber: e.target.value }))}
                                                    placeholder="000 - 111 - 01 - 01"
                                                />
                                            </div>
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Texto Superior:</label>
                                                <input 
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={newContactoItem.topText}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, topText: e.target.value }))}
                                                    placeholder="Envía tu mensaje de voz al:"
                                                />
                                            </div>
                                            <div className="col-md-6 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Texto Inferior (Footer):</label>
                                                <input 
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={newContactoItem.bottomText}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, bottomText: e.target.value }))}
                                                    placeholder="Powered by Interaktia"
                                                />
                                            </div>
                                        </div>

                                        <div className="row g-3 mt-1">
                                            <div className="col-md-4 col-6 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs">Fondo Ícono:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={newContactoItem.iconBgColor}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, iconBgColor: e.target.value }))}
                                                />
                                            </div>
                                            <div className="col-md-4 col-6 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs">Símbolo Ícono:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={newContactoItem.iconSymbolColor}
                                                    onChange={(e) => setNewContactoItem(prev => ({ ...prev, iconSymbolColor: e.target.value }))}
                                                />
                                            </div>
                                            <div className="col-md-4 col-12 d-flex flex-column gap-1">
                                                <label className="text-muted text-xs">Logo / Ícono:</label>
                                                <div className="d-flex gap-2">
                                                    <input 
                                                        type="file"
                                                        ref={contactItemLogoRef}
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={handleContactoItemLogoUpload}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-light flex-fill py-1.5 rounded-2 text-xs"
                                                        onClick={() => contactItemLogoRef.current.click()}
                                                    >
                                                        Subir Logo
                                                    </button>
                                                    {newContactoItem.iconDataUrl && (
                                                        <button 
                                                            type="button"
                                                            className="btn btn-danger px-3.5 py-1 text-xs rounded-2"
                                                            onClick={handleContactoItemLogoReset}
                                                        >
                                                            ✖
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end gap-2 mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                            {editingContactoItemId !== -1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-secondary py-2 px-4 text-xs font-semibold rounded-2 text-white"
                                                    onClick={() => {
                                                        setEditingContactoItemId(-1);
                                                        setNewContactoItem({
                                                            nombreCanal: '',
                                                            topText: 'Envía tu mensaje de voz al:',
                                                            phoneNumber: '',
                                                            bottomText: 'Powered by Interaktia',
                                                            iconBgColor: '#25d366',
                                                            iconSymbolColor: '#ffffff',
                                                            iconDataUrl: null
                                                        });
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-danger py-2 px-4 text-xs font-semibold rounded-2 text-white border-0"
                                                style={{ minWidth: '150px' }}
                                                onClick={handleContactoAdd}
                                            >
                                                {editingContactoItemId !== -1 ? 'Actualizar Canal' : 'Guardar Canal'}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Listado */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📋 Canales Registrados ({contactConfig.contactos?.length || 0})
                                        </h3>
                                        
                                        <div className="d-flex flex-column gap-2" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                                            {(!contactConfig.contactos || contactConfig.contactos.length === 0) ? (
                                                <span className="text-muted text-xs text-center py-4">No hay canales de contacto registrados. Registra uno arriba.</span>
                                            ) : (
                                                contactConfig.contactos.map((c, index) => {
                                                    const isAct = (c.isActive || c.IsActive) && contactConfig.visible;
                                                    return (
                                                        <div key={c.id || c.Id} className="d-flex align-items-center justify-content-between p-3 rounded-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                                            <div className="d-flex align-items-center gap-3">
                                                                {c.iconDataUrl ? (
                                                                    <img src={c.iconDataUrl} alt="Logo" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'contain', background: '#fff' }} />
                                                                ) : (
                                                                    <div className="d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', borderRadius: '50%', background: c.iconBgColor, color: c.iconSymbolColor, fontSize: '0.85rem', fontWeight: 'bold' }}>
                                                                        {c.nombreCanal.substring(0, 2).toUpperCase()}
                                                                    </div>
                                                                )}
                                                                <div className="d-flex flex-column">
                                                                    <span className="fw-bold text-sm text-white">{c.nombreCanal}</span>
                                                                    <small className="text-muted text-xs">{c.phoneNumber}</small>
                                                                </div>
                                                            </div>

                                                            <div className="d-flex align-items-center gap-2">
                                                                {/* Mostrar/Ocultar */}
                                                                <button
                                                                    type="button"
                                                                    className={`btn btn-xs py-1 px-3 rounded-pill fw-bold text-xs ${
                                                                        isAct ? 'btn-danger text-white border-0' : 'btn-outline-secondary'
                                                                    }`}
                                                                    style={{ minWidth: '85px' }}
                                                                    onClick={() => handleContactoSelect(c.id || c.Id, c.isActive || c.IsActive)}
                                                                >
                                                                    {isAct ? '✓ Ocultar' : 'Mostrar'}
                                                                </button>

                                                                {/* Reordenar */}
                                                                <div className="d-flex flex-column gap-0.5">
                                                                    <button type="button" className="btn btn-outline-light btn-xs p-0 px-1 border-0" disabled={index === 0} onClick={() => handleContactoMove(index, 'up')}>
                                                                        <i className="bi bi-caret-up-fill"></i>
                                                                    </button>
                                                                    <button type="button" className="btn btn-outline-light btn-xs p-0 px-1 border-0" disabled={index === contactConfig.contactos.length - 1} onClick={() => handleContactoMove(index, 'down')}>
                                                                        <i className="bi bi-caret-down-fill"></i>
                                                                    </button>
                                                                </div>

                                                                {/* Editar */}
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-outline-light btn-xs py-1 px-2.5 rounded-2 border-0" 
                                                                    onClick={() => {
                                                                        setEditingContactoItemId(c.id || c.Id);
                                                                        setNewContactoItem({
                                                                            nombreCanal: c.nombreCanal,
                                                                            topText: c.topText,
                                                                            phoneNumber: c.phoneNumber,
                                                                            bottomText: c.bottomText,
                                                                            iconBgColor: c.iconBgColor,
                                                                            iconSymbolColor: c.iconSymbolColor,
                                                                            iconDataUrl: c.iconDataUrl
                                                                        });
                                                                    }}
                                                                >
                                                                    <i className="bi bi-pencil-fill text-info"></i>
                                                                </button>

                                                                {/* Eliminar */}
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-outline-light btn-xs py-1 px-2.5 rounded-2 border-0"
                                                                    onClick={() => handleContactoDelete(c.id || c.Id)}
                                                                >
                                                                    <i className="bi bi-trash3-fill text-danger"></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Ajustes Estéticos Globales */}
                                <div className="col-lg-5 col-md-12 d-flex flex-column gap-3">
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            🎨 Diseño y Coordenadas Globales
                                        </h3>

                                        <div className="row g-2">
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Fondo Placa:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={contactConfig.innerBgColor}
                                                    onChange={(e) => handleContactStyleChange('innerBgColor', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Texto Canal:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={contactConfig.innerTextColor}
                                                    onChange={(e) => handleContactStyleChange('innerTextColor', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Borde:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={contactConfig.borderColor}
                                                    onChange={(e) => handleContactStyleChange('borderColor', e.target.value)}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Textos Sup/Inf:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={contactConfig.outerTextColor || '#ffffff'}
                                                    onChange={(e) => handleContactStyleChange('outerTextColor', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <hr style={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                                        {/* Posición vertical */}
                                        <div className="tombola-input-item d-flex flex-column gap-2">
                                            <label className="text-muted text-xs fw-semibold">Posición Vertical:</label>
                                            <div className="row g-2">
                                                {['top', 'center', 'bottom'].map((pos) => (
                                                    <div key={pos} className="col-4">
                                                        <button
                                                            type="button"
                                                            className={`btn w-100 py-2 rounded-2 text-xs fw-bold ${contactConfig.position === pos ? 'btn-danger border-0 text-white' : 'btn-outline-secondary'}`}
                                                            onClick={() => handleContactPositionChange(pos)}
                                                        >
                                                            {pos === 'top' ? 'Arriba' : pos === 'center' ? 'Centro' : 'Abajo'}
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Sliders X/Y */}
                                        {renderSliderWithInput("Ajuste X (Desplazamiento):", contactConfig.offsetX, 0, 100, 1, "%", (val) => handleContactStyleChange('offsetX', val))}
                                        {renderSliderWithInput("Ajuste Y (Desplazamiento):", contactConfig.offsetY, 0, 100, 1, "%", (val) => handleContactStyleChange('offsetY', val))}

                                        {/* Selector tipo de fondo */}
                                        <div className="tombola-input-item d-flex flex-column gap-1">
                                            <label className="text-muted text-xs fw-semibold">Tipo de Fondo:</label>
                                            <select
                                                className="form-select border-secondary text-white rounded-2"
                                                style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                value={contactConfig.bgType}
                                                onChange={(e) => handleContactStyleChange('bgType', e.target.value)}
                                            >
                                                <option value="color">Color Sólido</option>
                                                <option value="image">Imagen Personalizada</option>
                                                <option value="video">Video en Bucle</option>
                                            </select>
                                        </div>

                                        {contactConfig.bgType === 'color' && (
                                            <div className="tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Color de Fondo Placa:</label>
                                                <input
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '35px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={contactConfig.bgColor}
                                                    onChange={(e) => handleContactStyleChange('bgColor', e.target.value)}
                                                />
                                            </div>
                                        )}

                                        {contactConfig.bgType === 'image' && (
                                            <div className="tombola-input-item d-flex flex-column gap-2">
                                                <label className="text-muted text-xs fw-semibold">Subir Imagen de Fondo:</label>
                                                <div className="d-flex gap-2">
                                                    <input type="file" ref={bgImageRef} accept="image/*" style={{ display: 'none' }} onChange={(e) => handleBgMediaUpload(e, 'image')} />
                                                    <button type="button" className="btn btn-outline-light flex-fill py-2 rounded-2 text-sm fw-semibold" onClick={() => bgImageRef.current.click()}>Subir Imagen</button>
                                                    {contactConfig.bgMediaUrl && <button type="button" className="btn btn-danger px-3 py-2 rounded-2" onClick={handleBgMediaReset}>✖</button>}
                                                </div>
                                            </div>
                                        )}

                                        {contactConfig.bgType === 'video' && (
                                            <div className="tombola-input-item d-flex flex-column gap-2">
                                                <label className="text-muted text-xs fw-semibold">Subir Video (Max 8MB):</label>
                                                <div className="d-flex gap-2">
                                                    <input type="file" ref={bgVideoRef} accept="video/*" style={{ display: 'none' }} onChange={(e) => handleBgMediaUpload(e, 'video')} />
                                                    <button type="button" className="btn btn-outline-light flex-fill py-2 rounded-2 text-sm fw-semibold" onClick={() => bgVideoRef.current.click()}>Subir Video</button>
                                                    {contactConfig.bgMediaUrl && <button type="button" className="btn btn-danger px-3 py-2 rounded-2" onClick={handleBgMediaReset}>✖</button>}
                                                </div>
                                            </div>
                                        )}

                                        {/* Sliders visuales */}
                                        {renderSliderWithInput("Zoom Placa:", contactConfig.scale, 0.5, 6.0, 0.1, "x", (val) => handleContactStyleChange('scale', val))}
                                        {renderSliderWithInput("Zoom Ícono:", contactConfig.iconZoom, 0.6, 6.0, 0.05, "x", (val) => handleContactStyleChange('iconZoom', val))}
                                        {renderSliderWithInput("Grosor Contorno:", contactConfig.borderWidth, 0, 10, 1, "px", (val) => handleContactStyleChange('borderWidth', val))}

                                        {/* Dimensiones */}
                                        <div className="tombola-input-item mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                            {renderSliderWithInput("Ancho Placa:", contactConfig.outerWidth || 900, 500, 1100, 10, "px", (val) => handleContactStyleChange('outerWidth', val))}
                                            {renderSliderWithInput("Alto Placa:", contactConfig.outerHeight || 220, 150, 380, 5, "px", (val) => handleContactStyleChange('outerHeight', val))}
                                            {renderSliderWithInput("Ancho Número:", contactConfig.innerWidth || 780, 350, 850, 10, "px", (val) => handleContactStyleChange('innerWidth', val))}
                                            {renderSliderWithInput("Alto Número:", contactConfig.innerHeight || 92, 60, 150, 2, "px", (val) => handleContactStyleChange('innerHeight', val))}
                                        </div>

                                        {/* Previsualización */}
                                        <div className="tombola-preview-box rounded-3 text-center my-2" style={{ height: '150px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                            <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                Previsualización
                                            </span>
                                            <div style={{
                                                position: 'absolute',
                                                width: '1920px',
                                                height: '1080px',
                                                left: 'calc(50% - 160px)',
                                                top: '-20px',
                                                transform: 'scale(0.1666)',
                                                transformOrigin: 'top left',
                                                pointerEvents: 'none'
                                            }}>
                                                <ContactoOverlay config={{ ...contactConfig, visible: true }} />
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn w-100 py-2.5 rounded-2 text-white fw-bold text-sm btn-save mt-2"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            disabled={isLoading}
                                            onClick={handleSaveContactConfig}
                                        >
                                            ⚡ Guardar Ajustes Estéticos
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 3: PORTADA DE LA TÓMBOLA (COVER)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsCoverExpanded(!isCoverExpanded); setIsExpanded(false); setIsContactExpanded(false); setIsSoporteExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-image-fill me-1"></i>
                            <span>Portada de la Tómbola (Cover)</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: portadaConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: portadaConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${portadaConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {portadaConfig.visible ? '🟢 La portada se está mostrando en pantalla' : '🔴 La portada está oculta'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    portadaConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: portadaConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerTogglePortadaVisibility(!portadaConfig.visible);
                                }}
                            >
                                {portadaConfig.visible ? 'Ocultar portada' : 'Mostrar portada'}
                            </button>
                            <i className={`bi fs-5 ${isCoverExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isCoverExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Título: <strong className="text-white">"{portadaConfig.titleText}"</strong> | 
                                Fondo: <strong className="text-white">{portadaConfig.bgType === 'color' ? 'Color Sólido' : portadaConfig.bgType === 'image' ? 'Imagen' : 'Video'}</strong> | 
                                Estado: <strong className="text-white">{portadaConfig.expanded ? 'Pantalla Completa (Expandido)' : 'Banda Centrada'}</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsCoverExpanded(true); setIsExpanded(false); setIsContactExpanded(false); setIsSoporteExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {/* Cuerpo de Ajustes Expandido */}
                    {isCoverExpanded && (
                        <div className="card-body p-4" style={{ background: '#0e111a' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Títulos y Visibilidad */}
                                <div className="col-lg-4 col-md-6">
                                    <div className="tombola-card h-100 p-4 rounded-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📝 Títulos y Visibilidad
                                        </h3>

                                        <div className="tombola-form-group d-flex flex-column gap-3">
                                            
                                            {/* Imagen de Título */}
                                            <div className="tombola-input-item d-flex flex-column gap-2">
                                                <label className="text-white fw-semibold text-xs">Imagen de Título (Reemplaza texto):</label>
                                                <div className="d-flex gap-2">
                                                    <input
                                                        type="file"
                                                        ref={coverTitleImgRef}
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        onChange={handlePortadaTitleImgUpload}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-light flex-fill py-2 rounded-2 text-xs fw-semibold"
                                                        onClick={() => coverTitleImgRef.current.click()}
                                                    >
                                                        {portadaConfig.titleImgDataUrl ? 'Cambiar Imagen' : 'Subir Imagen de Título'}
                                                    </button>
                                                    {portadaConfig.titleImgDataUrl && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-danger px-3 py-2 rounded-2 text-xs"
                                                            onClick={handlePortadaTitleImgReset}
                                                        >
                                                            ✖
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Slider para ajustar tamaño de la imagen de título */}
                                            {portadaConfig.titleImgDataUrl && (
                                                <div className="tombola-slider-group">
                                                    <div className="slider-header d-flex justify-content-between text-xs mb-1">
                                                        <span className="text-muted">Ancho Imagen de Título:</span>
                                                        <span className="text-white fw-bold">{portadaConfig.titleImgWidth || 500}px</span>
                                                    </div>
                                                    <input
                                                        type="range"
                                                        className="w-100"
                                                        min="200"
                                                        max="1000"
                                                        step="10"
                                                        value={portadaConfig.titleImgWidth || 500}
                                                        onChange={(e) => handlePortadaTextChange('titleImgWidth', parseInt(e.target.value))}
                                                    />
                                                </div>
                                            )}

                                            {/* Texto de Título (Solo visible si no hay imagen de título) */}
                                            {!portadaConfig.titleImgDataUrl && (
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Título Principal (Texto):</label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-white border-secondary rounded-2"
                                                        style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                        value={portadaConfig.titleText}
                                                        onChange={(e) => handlePortadaTextChange('titleText', e.target.value)}
                                                        placeholder="LA TÓMBOLA PERRONA"
                                                    />
                                                </div>
                                            )}

                                            {/* Subtítulo */}
                                            <div className="tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Subtítulo (Opcional):</label>
                                                <input
                                                    type="text"
                                                    className="form-control text-white border-secondary rounded-2"
                                                    style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                    value={portadaConfig.subtitleText}
                                                    onChange={(e) => handlePortadaTextChange('subtitleText', e.target.value)}
                                                    placeholder="Ej: Gran Sorteo Especial"
                                                />
                                            </div>

                                            {/* Estatus e interruptor de proyección */}
                                            <div className="tombola-input-item mt-2 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                                <label className="text-muted text-xs fw-semibold d-block mb-1">Estatus del Overlay:</label>
                                                <div className="mb-2">
                                                    <span className={`fw-bold text-sm ${portadaConfig.visible ? 'text-success' : 'text-danger'}`}>
                                                        {portadaConfig.visible ? '🟢 La portada se está mostrando en pantalla' : '🔴 La portada está oculta'}
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => triggerTogglePortadaVisibility(!portadaConfig.visible)}
                                                    className={`btn w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                                        portadaConfig.visible 
                                                        ? 'btn-outline-danger' 
                                                        : 'btn-outline-success'
                                                    }`}
                                                    style={{ borderWidth: '2px' }}
                                                >
                                                    <i className={`bi ${portadaConfig.visible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                                    {portadaConfig.visible ? 'Ocultar portada' : 'Mostrar portada'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Fondo y Diseño */}
                                <div className="col-lg-4 col-md-6">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                🖼️ Fondo y Comportamiento
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-3">
                                                {/* Botón de Expansión de Fondo */}
                                                <div className="tombola-input-item pt-1">
                                                    <label className="text-muted text-xs fw-semibold d-block mb-2">Comportamiento del Fondo:</label>
                                                    <button
                                                        type="button"
                                                        className={`btn w-100 py-2.5 fw-bold text-sm transition-all rounded-3 ${
                                                            portadaConfig.expanded 
                                                                ? 'btn-success text-white' 
                                                                : 'btn-outline-secondary text-muted'
                                                        }`}
                                                        onClick={() => handlePortadaTextChange('expanded', !portadaConfig.expanded)}
                                                        style={{ border: '2px solid' }}
                                                    >
                                                        {portadaConfig.expanded ? (
                                                            <>
                                                                <i className="bi bi-arrows-fullscreen me-2"></i>
                                                                🟢 Fondo Completo (Expandido)
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-distribute-vertical me-2"></i>
                                                                ⚪ Banda Centrada (Columna)
                                                            </>
                                                        )}
                                                    </button>
                                                    <small className="text-muted d-block mt-1.5" style={{ fontSize: '0.75rem' }}>
                                                        {portadaConfig.expanded 
                                                            ? 'El fondo cubrirá la pantalla completa (1920x1080).' 
                                                            : 'El fondo se limitará a una columna central de 650px (lados transparentes).'}
                                                    </small>
                                                </div>

                                                <hr style={{ borderColor: 'rgba(255,255,255,0.06)' }} />

                                                {/* Selector tipo de fondo */}
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Tipo de Fondo:</label>
                                                    <select
                                                        className="form-select border-secondary text-white rounded-2"
                                                        style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                        value={portadaConfig.bgType}
                                                        onChange={(e) => handlePortadaTextChange('bgType', e.target.value)}
                                                    >
                                                        <option value="color">Color Sólido</option>
                                                        <option value="image">Imagen de Portada</option>
                                                        <option value="video">Video Loop de Fondo (Bienvenidos)</option>
                                                    </select>
                                                </div>

                                                {/* Color sólido de fondo */}
                                                {portadaConfig.bgType === 'color' && (
                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Color de Fondo:</label>
                                                        <input
                                                            type="color"
                                                            className="w-100 border-0 rounded-2 p-1"
                                                            style={{ height: '40px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                            value={portadaConfig.bgColor}
                                                            onChange={(e) => handlePortadaTextChange('bgColor', e.target.value)}
                                                        />
                                                    </div>
                                                )}

                                                {/* Subir imagen de fondo */}
                                                {portadaConfig.bgType === 'image' && (
                                                    <div className="tombola-input-item d-flex flex-column gap-2">
                                                        <label className="text-muted text-xs fw-semibold">Subir Imagen de Fondo:</label>
                                                        <div className="d-flex gap-2">
                                                            <input
                                                                type="file"
                                                                ref={coverBgImageRef}
                                                                accept="image/*"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => handlePortadaBgUpload(e, 'image')}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-light flex-fill py-2 rounded-2 text-sm fw-semibold"
                                                                onClick={() => coverBgImageRef.current.click()}
                                                            >
                                                                Subir Imagen
                                                            </button>
                                                            {portadaConfig.bgMediaUrl && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger px-3 py-2 rounded-2"
                                                                    onClick={handlePortadaBgReset}
                                                                >
                                                                    ✖
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Subir video de fondo */}
                                                {portadaConfig.bgType === 'video' && (
                                                    <div className="tombola-input-item d-flex flex-column gap-2">
                                                        <label className="text-muted text-xs fw-semibold">Subir Video Loop (Max 12MB):</label>
                                                        <div className="d-flex gap-2">
                                                            <input
                                                                type="file"
                                                                ref={coverBgVideoRef}
                                                                accept="video/*"
                                                                style={{ display: 'none' }}
                                                                onChange={(e) => handlePortadaBgUpload(e, 'video')}
                                                            />
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-light flex-fill py-2 rounded-2 text-sm fw-semibold"
                                                                onClick={() => coverBgVideoRef.current.click()}
                                                            >
                                                                Subir Video
                                                            </button>
                                                            {portadaConfig.bgMediaUrl && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-danger px-3 py-2 rounded-2"
                                                                    onClick={handlePortadaBgReset}
                                                                >
                                                                    ✖
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex-grow-1 d-flex flex-column justify-content-end mt-2">
                                            <div className="tombola-preview-box rounded-3 text-center" style={{ height: '180px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                                <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                    Previsualización (Lienzo 16:9)
                                                </span>
                                                <div style={{
                                                    position: 'absolute',
                                                    width: '1920px',
                                                    height: '1080px',
                                                    left: 'calc(50% - 160px)',
                                                    top: '0',
                                                    transform: 'scale(0.1666)',
                                                    transformOrigin: 'top left',
                                                    pointerEvents: 'none'
                                                }}>
                                                    <PortadaOverlay config={{ ...portadaConfig, visible: true }} isAdminPreview={true} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 3: Logo y Ajustes */}
                                <div className="col-lg-4 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                🎨 Logotipo y Dimensiones
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-2">
                                                {/* Subir Logo de Portada */}
                                                <div className="tombola-input-item d-flex flex-column gap-2 mb-2">
                                                    <label className="text-muted text-xs fw-semibold">Logotipo Superior de la Portada:</label>
                                                    <div className="d-flex gap-2">
                                                        <input
                                                            type="file"
                                                            ref={coverLogoRef}
                                                            accept="image/*"
                                                            style={{ display: 'none' }}
                                                            onChange={handlePortadaLogoUpload}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-light flex-fill py-2 rounded-2 text-xs fw-semibold"
                                                            onClick={() => coverLogoRef.current.click()}
                                                        >
                                                            Subir Logotipo
                                                        </button>
                                                        {portadaConfig.logoDataUrl && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-danger px-3 py-2 rounded-2 text-xs"
                                                                onClick={handlePortadaLogoReset}
                                                            >
                                                                ✖
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Ajustes de Logo */}
                                                <div className="logo-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <span className="text-white fw-bold text-xs d-block mb-2">Ajustes de Logotipo:</span>
                                                    {renderSliderWithInput("Zoom Logo:", portadaConfig.logoScale ?? 1.0, 0.2, 6.0, 0.05, "x", (val) => handlePortadaTextChange('logoScale', val))}
                                                    {renderSliderWithInput("Posición X (Logo):", portadaConfig.logoOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('logoOffsetX', val))}
                                                    {renderSliderWithInput("Posición Y (Logo):", portadaConfig.logoOffsetY ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('logoOffsetY', val))}
                                                </div>

                                                {/* Ajustes de Imagen de Título (solo si hay imagen subida) */}
                                                {portadaConfig.titleImgDataUrl && (
                                                    <div className="title-img-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <span className="text-white fw-bold text-xs d-block mb-2">Ajustes de Imagen de Título:</span>
                                                        {renderSliderWithInput("Zoom Imagen:", portadaConfig.titleImgScale ?? 1.0, 0.2, 3.0, 0.05, "x", (val) => handlePortadaTextChange('titleImgScale', val))}
                                                        {renderSliderWithInput("Ancho Imagen (Width):", portadaConfig.titleImgWidth ?? 500, 200, 1000, 10, "px", (val) => handlePortadaTextChange('titleImgWidth', val))}
                                                        {renderSliderWithInput("Posición X (Imagen):", portadaConfig.titleOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('titleOffsetX', val))}
                                                        {renderSliderWithInput("Posición Y (Imagen):", portadaConfig.titleOffsetY ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('titleOffsetY', val))}
                                                    </div>
                                                )}

                                                {/* Ajustes de Texto de Título (solo si NO hay imagen subida) */}
                                                {!portadaConfig.titleImgDataUrl && (
                                                    <div className="title-text-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <span className="text-white fw-bold text-xs d-block mb-2">Ajustes de Texto de Título:</span>
                                                        {renderSliderWithInput("Tamaño Letra Título:", portadaConfig.titleSize ?? 4.8, 1.0, 10.0, 0.1, "rem", (val) => handlePortadaTextChange('titleSize', val))}
                                                        {renderSliderWithInput("Posición X (Título):", portadaConfig.titleOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('titleOffsetX', val))}
                                                        {renderSliderWithInput("Posición Y (Título):", portadaConfig.titleOffsetY ?? 0, -1000, 1000, 5, "px", (val) => handlePortadaTextChange('titleOffsetY', val))}
                                                    </div>
                                                )}

                                                {/* Ajustes de Subtítulo */}
                                                {renderSliderWithInput("Tamaño Letra Subtítulo:", portadaConfig.subtitleSize ?? 2.2, 0.8, 6.0, 0.1, "rem", (val) => handlePortadaTextChange('subtitleSize', val))}

                                                {/* Zoom General de la Portada */}
                                                {renderSliderWithInput("Escala / Zoom General:", portadaConfig.scale, 0.5, 6.0, 0.05, "x", (val) => handlePortadaTextChange('scale', val))}

                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            <div className="tombola-admin-actions d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                <div className="text-muted text-xs d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    <span>💡 <strong>Consejo OBS:</strong> La portada se proyecta sobre el mismo canal de OBS. Si se expande a pantalla completa, tapará otros elementos de fondo.</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save"
                                    style={{ background: '#ff003c', border: 'none', minWidth: '220px', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                    disabled={isLoading}
                                    onClick={handleSavePortadaConfig}
                                >
                                    ⚡ Guardar y Aplicar Cambios
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 4: PANTALLA DE DETALLES TÉCNICOS (SOPORTE)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsSoporteExpanded(!isSoporteExpanded); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-wrench-adjustable me-1"></i>
                            <span>Pantalla de Detalles Técnicos</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: soporteConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: soporteConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${soporteConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {soporteConfig.visible ? '🟢 La pantalla técnica se está mostrando en pantalla' : '🔴 La pantalla técnica está oculta'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    soporteConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: soporteConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    triggerToggleSoporteVisibility(!soporteConfig.visible);
                                }}
                            >
                                {soporteConfig.visible ? 'Ocultar pantalla' : 'Mostrar pantalla'}
                            </button>
                            <i className={`bi fs-5 ${isSoporteExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isSoporteExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Título: <strong className="text-white">"{soporteConfig.titleText}"</strong> | 
                                Estatus: <strong className="text-white">{soporteConfig.visible ? 'Visible' : 'Oculto'}</strong> | 
                                Fondo: <strong className="text-white">{soporteConfig.expanded ? 'Pantalla Completa' : 'Banda Centrada'}</strong> | 
                                Zoom: <strong className="text-white">{soporteConfig.scale.toFixed(2)}x</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsSoporteExpanded(true); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {/* Cuerpo de Ajustes Expandido */}
                    {isSoporteExpanded && (
                        <div className="card-body p-4" style={{ background: '#0e111a' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Textos y Visibilidad */}
                                <div className="col-lg-4 col-md-6">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                📝 Textos y Proyección
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-3">
                                                {/* Título */}
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Título Principal:</label>
                                                    <input
                                                        type="text"
                                                        className="form-control text-white border-secondary rounded-2"
                                                        style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                        value={soporteConfig.titleText}
                                                        onChange={(e) => handleSoporteTextChange('titleText', e.target.value)}
                                                        placeholder="DETALLES TÉCNICOS"
                                                    />
                                                </div>

                                                {/* Color del Título */}
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Color del Título:</label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color border-0 bg-transparent"
                                                            style={{ width: '45px', height: '40px', cursor: 'pointer' }}
                                                            value={soporteConfig.titleColor}
                                                            onChange={(e) => handleSoporteTextChange('titleColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control text-white border-secondary rounded-2 flex-grow-1"
                                                            style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                            value={soporteConfig.titleColor}
                                                            onChange={(e) => handleSoporteTextChange('titleColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Descripción */}
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Descripción / Mensaje Técnico:</label>
                                                    <textarea
                                                        className="form-control text-white border-secondary rounded-2"
                                                        style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', height: '80px', resize: 'none' }}
                                                        value={soporteConfig.descriptionText}
                                                        onChange={(e) => handleSoporteTextChange('descriptionText', e.target.value)}
                                                        placeholder="Describe los detalles técnicos..."
                                                    />
                                                </div>

                                                {/* Color de la Descripción */}
                                                <div className="tombola-input-item d-flex flex-column gap-1">
                                                    <label className="text-muted text-xs fw-semibold">Color del Mensaje:</label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color border-0 bg-transparent"
                                                            style={{ width: '45px', height: '40px', cursor: 'pointer' }}
                                                            value={soporteConfig.descriptionColor}
                                                            onChange={(e) => handleSoporteTextChange('descriptionColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control text-white border-secondary rounded-2 flex-grow-1"
                                                            style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                            value={soporteConfig.descriptionColor}
                                                            onChange={(e) => handleSoporteTextChange('descriptionColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="tombola-input-item mt-auto pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                            <label className="text-muted text-xs fw-semibold d-block mb-1">Estatus del Overlay:</label>
                                            <div className="mb-2">
                                                <span className={`fw-bold text-sm ${soporteConfig.visible ? 'text-success' : 'text-danger'}`}>
                                                    {soporteConfig.visible ? '🟢 Se está mostrando en pantalla' : '🔴 El banner está oculto'}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => triggerToggleSoporteVisibility(!soporteConfig.visible)}
                                                className={`btn w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                                    soporteConfig.visible 
                                                    ? 'btn-outline-danger' 
                                                    : 'btn-outline-success'
                                                }`}
                                                style={{ borderWidth: '2px' }}
                                            >
                                                <i className={`bi ${soporteConfig.visible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                                {soporteConfig.visible ? 'Ocultar Proyección' : 'Transmitir Proyección'}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Fondo y Estilo */}
                                <div className="col-lg-4 col-md-6">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                🎨 Fondo y Comportamiento
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-2 mb-3">
                                                {/* Color de Fondo */}
                                                <div className="tombola-input-item d-flex flex-column gap-1 mb-2">
                                                    <label className="text-muted text-xs fw-semibold">Color de Fondo del Banner:</label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color border-0 bg-transparent"
                                                            style={{ width: '45px', height: '40px', cursor: 'pointer' }}
                                                            value={soporteConfig.bgColor}
                                                            onChange={(e) => handleSoporteTextChange('bgColor', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control text-white border-secondary rounded-2 flex-grow-1"
                                                            style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                            value={soporteConfig.bgColor}
                                                            onChange={(e) => handleSoporteTextChange('bgColor', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Botón de Expansión de Fondo */}
                                                <div className="tombola-input-item">
                                                    <label className="text-muted text-xs fw-semibold d-block mb-1">Expandir Banner a Pantalla Completa:</label>
                                                    <button
                                                        type="button"
                                                        className={`btn w-100 py-2 fw-bold rounded-2 d-flex align-items-center justify-content-center gap-2 transition-all ${
                                                            soporteConfig.expanded
                                                            ? 'btn-danger'
                                                            : 'btn-outline-secondary'
                                                        }`}
                                                        style={{ border: '2px solid' }}
                                                        onClick={() => handleSoporteTextChange('expanded', !soporteConfig.expanded)}
                                                    >
                                                        <i className={`bi ${soporteConfig.expanded ? 'bi-fullscreen-exit' : 'bi-fullscreen'}`}></i>
                                                        {soporteConfig.expanded ? 'Fondo Completo (Desactivar)' : 'Expandir Fondo Completo'}
                                                    </button>
                                                </div>
                                            </div>

                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                ✨ Estilo y Efecto Neón
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-2">
                                                {/* Color de Acento */}
                                                <div className="tombola-input-item d-flex flex-column gap-1 mb-2">
                                                    <label className="text-muted text-xs fw-semibold">Color de Acento / Neón:</label>
                                                    <div className="d-flex gap-2 align-items-center">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color border-0 bg-transparent"
                                                            style={{ width: '45px', height: '40px', cursor: 'pointer' }}
                                                            value={soporteConfig.color}
                                                            onChange={(e) => handleSoporteTextChange('color', e.target.value)}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control text-white border-secondary rounded-2 flex-grow-1"
                                                            style={{ background: 'rgba(0,0,0,0.3)', padding: '10px' }}
                                                            value={soporteConfig.color}
                                                            onChange={(e) => handleSoporteTextChange('color', e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {/* Slider Brillo */}
                                                {renderSliderWithInput("Brillo Neón (Glow):", soporteConfig.glow ?? 30, 0, 100, 1, "px", (val) => handleSoporteTextChange('glow', val))}

                                                {/* Slider Velocidad */}
                                                {renderSliderWithInput("Velocidad de Barrido:", soporteConfig.speed ?? 1.8, 0.5, 5.0, 0.1, "s", (val) => handleSoporteTextChange('speed', val))}
                                            </div>
                                        </div>

                                        <div className="flex-grow-1 d-flex flex-column justify-content-end mt-2">
                                            <div className="tombola-preview-box rounded-3 text-center" style={{ height: '180px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                                <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                    Previsualización (Lienzo 16:9)
                                                </span>
                                                <div style={{
                                                    position: 'absolute',
                                                    width: '1920px',
                                                    height: '1080px',
                                                    left: 'calc(50% - 160px)',
                                                    top: '0',
                                                    transform: 'scale(0.1666)',
                                                    transformOrigin: 'top left',
                                                    pointerEvents: 'none'
                                                }}>
                                                    <SoporteOverlay config={{ ...soporteConfig, visible: true }} isAdminPreview={true} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 3: Posición, Coordenadas y Tamaño */}
                                <div className="col-lg-4 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', maxHeight: '550px', overflowY: 'auto' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                📍 Posicionamiento y Zoom
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-2">
                                                {/* Atajo de Posición */}
                                                <div className="tombola-input-item d-flex flex-column gap-1 mb-2">
                                                    <label className="text-muted text-xs fw-semibold">Ubicación Predefinida (Banner):</label>
                                                    <select
                                                        className="form-select bg-dark text-white border-secondary rounded-2"
                                                        style={{ padding: '10px' }}
                                                        value={soporteConfig.position}
                                                        onChange={(e) => handleSoportePositionChange(e.target.value)}
                                                    >
                                                        <option value="center">Centro de la pantalla</option>
                                                        <option value="top">Superior Centrado</option>
                                                        <option value="bottom">Inferior Centrado</option>
                                                        <option value="custom">Alineación Manual (Personalizado)</option>
                                                    </select>
                                                </div>

                                                {/* Ajustes de Coordenadas Manuales (si es personalizado) */}
                                                {soporteConfig.position === 'custom' && (
                                                    <div className="coords-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <span className="text-white fw-bold text-xs d-block mb-2">Posición General del Contenido:</span>
                                                        {renderSliderWithInput("Coordenada X (General):", soporteConfig.offsetX ?? 960, 0, 1920, 5, "px", (val) => handleSoporteTextChange('offsetX', val))}
                                                        {renderSliderWithInput("Coordenada Y (General):", soporteConfig.offsetY ?? 540, 0, 1080, 5, "px", (val) => handleSoporteTextChange('offsetY', val))}
                                                        {renderSliderWithInput("Zoom General Banner:", soporteConfig.scale ?? 1.0, 0.4, 6.0, 0.05, "x", (val) => handleSoporteTextChange('scale', val))}
                                                    </div>
                                                )}

                                                {/* Zoom General */}
                                                {soporteConfig.position !== 'custom' && renderSliderWithInput("Zoom General Banner:", soporteConfig.scale ?? 1.0, 0.4, 2.5, 0.05, "x", (val) => handleSoporteTextChange('scale', val))}

                                                {/* Ajustes de Título */}
                                                <div className="title-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <span className="text-white fw-bold text-xs d-block mb-2">Ajustes del Título Principal:</span>
                                                    {renderSliderWithInput("Tamaño Letra Título:", soporteConfig.titleSize ?? 2.0, 0.8, 6.0, 0.1, "rem", (val) => handleSoporteTextChange('titleSize', val))}
                                                    {renderSliderWithInput("Desplazamiento X (Título):", soporteConfig.titleOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('titleOffsetX', val))}
                                                    {renderSliderWithInput("Desplazamiento Y (Título):", soporteConfig.titleOffsetY ?? -80, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('titleOffsetY', val))}
                                                </div>

                                                {/* Ajustes de Mensaje / Descripción */}
                                                <div className="desc-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <span className="text-white fw-bold text-xs d-block mb-2">Ajustes del Mensaje Técnico:</span>
                                                    {renderSliderWithInput("Tamaño Letra Mensaje:", soporteConfig.descSize ?? 1.0, 0.6, 4.0, 0.05, "rem", (val) => handleSoporteTextChange('descSize', val))}
                                                    {renderSliderWithInput("Desplazamiento X (Mensaje):", soporteConfig.descOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('descOffsetX', val))}
                                                    {renderSliderWithInput("Desplazamiento Y (Mensaje):", soporteConfig.descOffsetY ?? 50, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('descOffsetY', val))}
                                                </div>

                                                {/* Ajustes de Acento */}
                                                <div className="accent-adjustments-group p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <span className="text-white fw-bold text-xs d-block mb-2">Ajustes del Acento (Ícono y Barra):</span>
                                                    {renderSliderWithInput("Zoom Acento:", soporteConfig.accentScale ?? 1.0, 0.3, 6.0, 0.05, "x", (val) => handleSoporteTextChange('accentScale', val))}
                                                    {renderSliderWithInput("Desplazamiento X (Acento):", soporteConfig.accentOffsetX ?? 0, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('accentOffsetX', val))}
                                                    {renderSliderWithInput("Desplazamiento Y (Acento):", soporteConfig.accentOffsetY ?? -220, -1000, 1000, 5, "px", (val) => handleSoporteTextChange('accentOffsetY', val))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>

                            <div className="tombola-admin-actions d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                <div className="text-muted text-xs d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    <span>💡 <strong>Consejo OBS:</strong> La pantalla técnica se proyecta sobre el overlay general de la tómbola. Si se activa, puede tapar la tómbola o placas de contacto activas.</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save"
                                    style={{ background: '#ff003c', border: 'none', minWidth: '220px', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                    disabled={isLoading}
                                    onClick={handleSaveSoporteConfig}
                                >
                                    ⚡ Guardar y Aplicar Cambios
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 5: PANTALLA DE CUMPLEAÑEROS
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsCumpleanosExpanded(!isCumpleanosExpanded); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-cake2-fill me-1"></i>
                            <span>Pantalla de Cumpleañeros</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: cumpleanosConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: cumpleanosConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${cumpleanosConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {cumpleanosConfig.visible ? '🟢 El banner de cumpleañeros se está mostrando en pantalla' : '🔴 El banner está oculto'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    cumpleanosConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: cumpleanosConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => { e.stopPropagation(); triggerToggleCumpleanosVisibility(!cumpleanosConfig.visible); }}
                            >
                                {cumpleanosConfig.visible ? 'Ocultar pantalla' : 'Mostrar pantalla'}
                            </button>
                            <i className={`bi fs-5 ${isCumpleanosExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isCumpleanosExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Título: <strong className="text-white">"{cumpleanosConfig.titleText}"</strong> | 
                                Estatus: <strong className="text-white">{cumpleanosConfig.visible ? 'Visible' : 'Oculto'}</strong> | 
                                Confeti: <strong className="text-white">{cumpleanosConfig.showConfetti ? 'Activo' : 'Inactivo'}</strong> | 
                                Rotación: <strong className="text-white">{cumpleanosConfig.rotationSpeed}s</strong> | 
                                Festejados: <strong className="text-white">{getBirthdayList().length} nombres</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsCumpleanosExpanded(true); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {isCumpleanosExpanded && (
                        <div className="card-body p-4" style={{ background: '#111522' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Listado de Cumpleañeros (Festejados) */}
                                <div className="col-lg-5 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div>
                                            <h3 className="fs-6 fw-bold text-uppercase mb-3 pb-2" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                🎂 Festejados
                                            </h3>

                                            <div className="tombola-form-group d-flex flex-column gap-2 mb-3">
                                                <label className="text-muted text-xs fw-semibold">Nombre del cumpleañero:</label>
                                                <div className="d-flex gap-2">
                                                    <input 
                                                        type="text" 
                                                        className="form-control bg-dark text-white border-secondary rounded-2"
                                                        placeholder="Nombre completo"
                                                        value={newBirthdayName}
                                                        onChange={(e) => setNewBirthdayName(e.target.value)}
                                                        onKeyDown={(e) => e.key === 'Enter' && handleAddBirthday()}
                                                    />
                                                    <button 
                                                        type="button" 
                                                        className="btn btn-danger px-3 rounded-2 fw-bold text-sm"
                                                        style={{ background: '#ff003c', border: 'none' }}
                                                        onClick={handleAddBirthday}
                                                    >
                                                        Agregar
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="birthday-list-container mt-3 p-3 rounded-3" style={{ border: '1.5px solid #dc3545', background: '#ffffff' }}>
                                                <span className="fw-bold text-dark text-sm mb-3 d-block">
                                                    Listado de cumpleañeros ({getBirthdayList().length})
                                                </span>
                                                <div className="d-flex flex-column gap-2" style={{ maxHeight: '350px', overflowY: 'auto' }}>
                                                    {getBirthdayList().map((name, index) => (
                                                        <div key={index} className="d-flex align-items-center justify-content-between py-2" style={{ borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                                                            {editingBirthdayIndex === index ? (
                                                                <input 
                                                                    type="text"
                                                                    className="form-control form-control-sm bg-light text-dark border-secondary"
                                                                    value={editingBirthdayName}
                                                                    onChange={(e) => setEditingBirthdayName(e.target.value)}
                                                                    onBlur={handleSaveEditBirthday}
                                                                    onKeyDown={(e) => e.key === 'Enter' && handleSaveEditBirthday()}
                                                                    autoFocus
                                                                />
                                                            ) : (
                                                                <span className="text-dark text-sm fw-semibold truncate-text" style={{ maxWidth: '150px' }}>{name}</span>
                                                            )}

                                                            <div className="d-flex align-items-center gap-1">
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-xs p-1 border-0"
                                                                    style={{ background: 'transparent' }}
                                                                    onClick={() => handleMoveBirthday(index, 'up')}
                                                                    disabled={index === 0}
                                                                    title="Subir"
                                                                >
                                                                    <i className="bi bi-caret-up-fill fs-5" style={{ color: index === 0 ? '#cccccc' : '#000000' }}></i>
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-xs p-1 border-0"
                                                                    style={{ background: 'transparent' }}
                                                                    onClick={() => handleMoveBirthday(index, 'down')}
                                                                    disabled={index === getBirthdayList().length - 1}
                                                                    title="Bajar"
                                                                >
                                                                    <i className="bi bi-caret-down-fill fs-5" style={{ color: index === getBirthdayList().length - 1 ? '#cccccc' : '#000000' }}></i>
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-xs p-1 border-0 ms-1"
                                                                    style={{ background: 'transparent' }}
                                                                    onClick={() => handleStartEditBirthday(index)}
                                                                    title="Editar"
                                                                >
                                                                    <i className="bi bi-pencil-square fs-5" style={{ color: '#000000' }}></i>
                                                                </button>
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-xs p-1 border-0"
                                                                    style={{ background: 'transparent' }}
                                                                    onClick={() => handleDeleteBirthday(index)}
                                                                    title="Eliminar"
                                                                >
                                                                    <i className="bi bi-trash-fill fs-5" style={{ color: '#000000' }}></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {getBirthdayList().length === 0 && (
                                                        <div className="text-center py-3 text-muted text-xs italic">
                                                            No hay cumpleañeros en la lista.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Diseño y Configuración de Pantalla */}
                                <div className="col-lg-7 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <div className="row g-4">
                                            
                                            {/* Subcolumna A: Reproductor, Efectos y Estilos */}
                                            <div className="col-md-6 d-flex flex-column gap-3" style={{ borderRight: '1px solid rgba(255,255,255,0.05)' }}>
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    ▶️ Reproductor y Estilos
                                                </h3>

                                                <div className="tombola-form-group d-flex flex-column gap-3">
                                                    {/* El reproductor se maneja de forma rápida arriba. Las configuraciones abajo son para estilos y personalización */}

                                                    {/* Lluvia de Confeti */}
                                                    <div className="tombola-input-item d-flex align-items-center justify-content-between py-2 rounded" style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', padding: '10px' }}>
                                                        <label className="text-white text-xs fw-bold m-0">Lluvia de Confeti Animada:</label>
                                                        <div className="form-check form-switch m-0">
                                                            <input 
                                                                className="form-check-input" 
                                                                type="checkbox" 
                                                                role="switch" 
                                                                checked={cumpleanosConfig.showConfetti}
                                                                onChange={(e) => updateCumpleanosConfigAndBroadcast({ showConfetti: e.target.checked })}
                                                                style={{ cursor: 'pointer', transform: 'scale(1.2)' }}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Opacidad del Confeti */}
                                                    {cumpleanosConfig.showConfetti && renderSliderWithInput("Opacidad del Confeti:", cumpleanosConfig.confettiOpacity ?? 0.8, 0.0, 1.0, 0.05, "x", (val) => updateCumpleanosConfigAndBroadcast({ confettiOpacity: val }))}

                                                    {/* Tiempo de cambio / Rotación */}
                                                    {renderSliderWithInput("Intervalo de Rotación:", cumpleanosConfig.rotationSpeed ?? 5, 2, 20, 1, "s", (val) => updateCumpleanosConfigAndBroadcast({ rotationSpeed: val }))}

                                                    {/* Orientación del Banner: Horizontal / Vertical */}
                                                    <div className="tombola-input-item d-flex align-items-center justify-content-between py-2 rounded" style={{ background: 'rgba(255,255,255,0.01)', border: '1px solid rgba(255,255,255,0.03)', padding: '10px' }}>
                                                        <label className="text-white text-xs fw-bold m-0">Diseño del Banner:</label>
                                                        <div className="btn-group" role="group">
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm px-3 fw-bold rounded-start-pill ${!cumpleanosConfig.isVertical ? 'btn-danger text-white' : 'btn-outline-secondary text-muted'}`}
                                                                onClick={() => handleCumpleanosTextChange('isVertical', false)}
                                                            >
                                                                Horizontal
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`btn btn-sm px-3 fw-bold rounded-end-pill ${cumpleanosConfig.isVertical ? 'btn-danger text-white' : 'btn-outline-secondary text-muted'}`}
                                                                onClick={() => handleCumpleanosTextChange('isVertical', true)}
                                                            >
                                                                Vertical
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Selector de Colores */}
                                                    <div className="d-flex gap-2">
                                                        <div className="tombola-input-item d-flex flex-column gap-1 flex-fill">
                                                            <label className="text-muted text-xs fw-semibold">Color Título:</label>
                                                            <input 
                                                                type="color" 
                                                                className="form-control form-control-color w-100 bg-dark border-secondary rounded-2"
                                                                value={cumpleanosConfig.titleColor}
                                                                onChange={(e) => handleCumpleanosTextChange('titleColor', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="tombola-input-item d-flex flex-column gap-1 flex-fill">
                                                            <label className="text-muted text-xs fw-semibold">Color Nombre:</label>
                                                            <input 
                                                                type="color" 
                                                                className="form-control form-control-color w-100 bg-dark border-secondary rounded-2"
                                                                value={cumpleanosConfig.nameColor}
                                                                onChange={(e) => handleCumpleanosTextChange('nameColor', e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="tombola-input-item d-flex flex-column gap-1 flex-fill">
                                                            <label className="text-muted text-xs fw-semibold">Color Fondo:</label>
                                                            <input 
                                                                type="color" 
                                                                className="form-control form-control-color w-100 bg-dark border-secondary rounded-2"
                                                                value={cumpleanosConfig.bgColor}
                                                                onChange={(e) => handleCumpleanosTextChange('bgColor', e.target.value)}
                                                            />
                                                        </div>
                                                    </div>

                                                    {/* Carga de Logotipos en los Extremos */}
                                                    <div className="d-flex gap-2">
                                                        <div className="tombola-input-item d-flex flex-column gap-1 flex-fill">
                                                            <label className="text-muted text-xs fw-semibold">Logo Izquierdo:</label>
                                                            <input 
                                                                type="file" 
                                                                ref={logoLeftRef}
                                                                accept="image/*"
                                                                className="d-none"
                                                                onChange={handleLogoLeftUpload}
                                                            />
                                                            <div className="d-flex gap-1">
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                                    onClick={() => logoLeftRef.current?.click()}
                                                                >
                                                                    Subir
                                                                </button>
                                                                {cumpleanosConfig.logoLeftDataUrl && (
                                                                    <button type="button" className="btn btn-xs btn-danger px-2 border-0" onClick={handleLogoLeftReset} title="Limpiar"><i className="bi bi-x"></i></button>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="tombola-input-item d-flex flex-column gap-1 flex-fill">
                                                            <label className="text-muted text-xs fw-semibold">Logo Derecho:</label>
                                                            <input 
                                                                type="file" 
                                                                ref={logoRightRef}
                                                                accept="image/*"
                                                                className="d-none"
                                                                onChange={handleLogoRightUpload}
                                                            />
                                                            <div className="d-flex gap-1">
                                                                <button 
                                                                    type="button" 
                                                                    className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                                    onClick={() => logoRightRef.current?.click()}
                                                                >
                                                                    Subir
                                                                </button>
                                                                {cumpleanosConfig.logoRightDataUrl && (
                                                                    <button type="button" className="btn btn-xs btn-danger px-2 border-0" onClick={handleLogoRightReset} title="Limpiar"><i className="bi bi-x"></i></button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Subcolumna B: Ubicación, Zoom y Previsualización */}
                                            <div className="col-md-6 d-flex flex-column gap-3">
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    📍 Posicionamiento
                                                </h3>

                                                <div className="tombola-form-group d-flex flex-column gap-2">
                                                    {/* Previsualizador */}
                                                    <div className="mb-2">
                                                        <div className="tombola-preview-box rounded-3 text-center" style={{ height: '140px', background: 'radial-gradient(circle at center, #1b2130 0%, #0d1017 100%)', position: 'relative', overflow: 'hidden', border: '1px dashed rgba(255,255,255,0.1)' }}>
                                                            <span className="watermark text-uppercase text-xs" style={{ position: 'absolute', top: '8px', left: '8px', color: 'rgba(255,255,255,0.04)', fontSize: '9px', fontWeight: 'bold' }}>
                                                                Previsualización (OBS)
                                                            </span>
                                                            <div style={{
                                                                position: 'absolute',
                                                                width: '1920px',
                                                                height: '1080px',
                                                                left: 'calc(50% - 160px)',
                                                                top: '0',
                                                                transform: 'scale(0.1666)',
                                                                transformOrigin: 'top left',
                                                                pointerEvents: 'none'
                                                            }}>
                                                                <CumpleanosOverlay config={{ ...cumpleanosConfig, visible: true }} />
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Atajo de Posición */}
                                                    <div className="tombola-input-item d-flex flex-column gap-1 mb-2">
                                                        <label className="text-muted text-xs fw-semibold">Ubicación Predefinida:</label>
                                                        <select
                                                            className="form-select bg-dark text-white border-secondary rounded-2"
                                                            style={{ padding: '8px', fontSize: '0.8rem' }}
                                                            value={cumpleanosConfig.position}
                                                            onChange={(e) => handleCumpleanosPositionChange(e.target.value)}
                                                        >
                                                            <option value="bottom">Inferior Centrado</option>
                                                            <option value="center">Centro de la pantalla</option>
                                                            <option value="top">Superior Centrado</option>
                                                            <option value="custom">Alineación Manual (Personalizado)</option>
                                                        </select>
                                                    </div>

                                                    {/* Ajustes de Coordenadas Manuales */}
                                                    {cumpleanosConfig.position === 'custom' && (
                                                        <div className="coords-adjustments-group p-2 rounded mb-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                            {renderSliderWithInput("Coordenada X (Manual):", cumpleanosConfig.offsetX ?? 50, 0, 100, 1, "%", (val) => handleCumpleanosTextChange('offsetX', val))}
                                                            {renderSliderWithInput("Coordenada Y (Manual):", cumpleanosConfig.offsetY ?? 85, 0, 100, 1, "%", (val) => handleCumpleanosTextChange('offsetY', val))}
                                                            {renderSliderWithInput("Zoom General Banner:", cumpleanosConfig.scale ?? 1.0, 0.4, 6.0, 0.05, "x", (val) => handleCumpleanosTextChange('scale', val))}
                                                        </div>
                                                    )}

                                                    {/* Zoom General */}
                                                    {cumpleanosConfig.position !== 'custom' && renderSliderWithInput("Zoom General Banner:", cumpleanosConfig.scale ?? 1.0, 0.4, 2.5, 0.05, "x", (val) => handleCumpleanosTextChange('scale', val))}
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                </div>

                            </div>

                            <div className="tombola-admin-actions d-flex justify-content-between align-items-center mt-4 pt-3" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                                <div className="text-muted text-xs d-flex align-items-center gap-1" style={{ fontSize: '0.85rem' }}>
                                    <span>💡 <strong>Consejo OBS:</strong> El banner de cumpleañeros se proyecta con efecto confeti dinámico. Configura la rotación de nombres si son más de uno.</span>
                                </div>
                                <button
                                    type="button"
                                    className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save"
                                    style={{ background: '#ff003c', border: 'none', minWidth: '220px', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                    disabled={isLoading}
                                    onClick={handleSaveCumpleanosConfig}
                                >
                                    ⚡ Guardar y Aplicar Cambios
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA 6: PANTALLA DE MENCIONES
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { setIsMencionesExpanded(!isMencionesExpanded); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); setIsCumpleanosExpanded(false); }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-megaphone-fill me-1"></i>
                            <span>Pantalla de Menciones</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: mencionesConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: mencionesConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${mencionesConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {mencionesConfig.visible ? '🟢 Las menciones se están mostrando en pantalla' : '🔴 Las menciones están ocultas'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    mencionesConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: mencionesConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={(e) => { e.stopPropagation(); handleMencionesVisibilityToggle(); }}
                            >
                                {mencionesConfig.visible ? 'Ocultar menciones' : 'Mostrar menciones'}
                            </button>
                            <i className={`bi fs-5 ${isMencionesExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isMencionesExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Estatus: <strong className="text-white">{mencionesConfig.visible ? 'Visible' : 'Oculto'}</strong> | 
                                Registradas: <strong className="text-white">{mencionesConfig.menciones.length} menciones</strong> | 
                                Activa: <strong className="text-white">"{mencionesConfig.activeMencion?.nombreInterno || 'Ninguna'}"</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { setIsMencionesExpanded(true); setIsExpanded(false); setIsContactExpanded(false); setIsCoverExpanded(false); setIsSoporteExpanded(false); setIsCumpleanosExpanded(false); }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {isMencionesExpanded && (
                        <div className="card-body p-4" style={{ background: '#111522' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Formulario Creador/Editor y Listado de Menciones */}
                                <div className="col-lg-7 col-md-12">
                                    <div className="row g-3">
                                        {/* Formulario */}
                                        <div className="col-md-6">
                                            <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    {editingMencionId !== -1 ? '📝 Editar Mención' : '✨ Nueva Mención'}
                                                </h3>

                                                <div className="tombola-form-group d-flex flex-column gap-2" style={{ fontSize: '0.8rem' }}>
                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Nombre Interno:</label>
                                                        <input 
                                                            type="text"
                                                            className="form-control bg-dark text-white border-secondary rounded-2"
                                                            placeholder="Ej: Anuncio Caliente"
                                                            value={newMencion.nombreInterno}
                                                            onChange={(e) => setNewMencion(prev => ({ ...prev, nombreInterno: e.target.value }))}
                                                        />
                                                    </div>

                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Texto de la Mención:</label>
                                                        <input 
                                                            type="text"
                                                            className="form-control bg-dark text-white border-secondary rounded-2"
                                                            placeholder="Ej: Compra tus boletos"
                                                            value={newMencion.texto}
                                                            onChange={(e) => setNewMencion(prev => ({ ...prev, texto: e.target.value }))}
                                                        />
                                                    </div>

                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <div className="row g-2">
                                                            <div className="col-6">
                                                                <label className="text-muted text-xs fw-semibold">Logo Izq / Logo 1:</label>
                                                                <input 
                                                                    type="file"
                                                                    ref={mencionesLogoRef}
                                                                    accept="image/*"
                                                                    className="d-none"
                                                                    onChange={handleMencionLogoUpload}
                                                                />
                                                                <div className="d-flex gap-1 align-items-center">
                                                                    <button 
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                                        onClick={() => mencionesLogoRef.current?.click()}
                                                                    >
                                                                        Subir
                                                                    </button>
                                                                    {newMencion.logoDataUrl && (
                                                                        <button 
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger px-2 border-0"
                                                                            onClick={() => setNewMencion(prev => ({ ...prev, logoDataUrl: null }))}
                                                                        >
                                                                            ✖
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {newMencion.logoDataUrl && (
                                                                    <div className="text-center mt-1">
                                                                        <img src={newMencion.logoDataUrl} alt="Preview 1" style={{ maxHeight: '35px', objectFit: 'contain', border: '1px solid rgba(255,255,255,0.1)', padding: '2px', background: '#fff', borderRadius: '4px' }} />
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="col-6">
                                                                <label className="text-muted text-xs fw-semibold">Logo Der / Logo 2:</label>
                                                                <input 
                                                                    type="file"
                                                                    ref={mencionesLogoRightRef}
                                                                    accept="image/*"
                                                                    className="d-none"
                                                                    onChange={handleMencionLogoRightUpload}
                                                                />
                                                                <div className="d-flex gap-1 align-items-center">
                                                                    <button 
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                                        onClick={() => mencionesLogoRightRef.current?.click()}
                                                                    >
                                                                        Subir
                                                                    </button>
                                                                    {newMencion.logoRightDataUrl && (
                                                                        <button 
                                                                            type="button"
                                                                            className="btn btn-sm btn-danger px-2 border-0"
                                                                            onClick={() => setNewMencion(prev => ({ ...prev, logoRightDataUrl: null }))}
                                                                        >
                                                                            ✖
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {newMencion.logoRightDataUrl && (
                                                                    <div className="text-center mt-1">
                                                                        <img src={newMencion.logoRightDataUrl} alt="Preview 2" style={{ maxHeight: '35px', objectFit: 'contain', border: '1px solid rgba(255,255,255,0.1)', padding: '2px', background: '#fff', borderRadius: '4px' }} />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Link / URL:</label>
                                                        <input 
                                                            type="text"
                                                            className="form-control bg-dark text-white border-secondary rounded-2"
                                                            placeholder="www.tickets.com"
                                                            value={newMencion.link}
                                                            onChange={(e) => setNewMencion(prev => ({ ...prev, link: e.target.value }))}
                                                        />
                                                    </div>

                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Formato:</label>
                                                        <select
                                                            className="form-select bg-dark text-white border-secondary rounded-2"
                                                            value={newMencion.formato}
                                                            onChange={(e) => setNewMencion(prev => ({ ...prev, formato: e.target.value }))}
                                                        >
                                                            <option value="Formato A">Formato A (Logo Izq + Link Der)</option>
                                                            <option value="Formato B">Formato B (Texto + Logos + Link Abajo)</option>
                                                        </select>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="btn btn-danger py-2 rounded-2 fw-bold text-sm w-100 mt-2"
                                                        style={{ background: '#ff003c', border: 'none' }}
                                                        onClick={handleMencionAdd}
                                                    >
                                                        {editingMencionId !== -1 ? 'Guardar Cambios' : 'Agregar'}
                                                    </button>

                                                    {editingMencionId !== -1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-outline-secondary py-1 text-xs w-100"
                                                            onClick={() => {
                                                                setEditingMencionId(-1);
                                                                setNewMencion({ nombreInterno: '', texto: '', link: '', formato: 'Formato A', logoDataUrl: null, logoRightDataUrl: null });
                                                                if (mencionesLogoRef.current) mencionesLogoRef.current.value = '';
                                                                if (mencionesLogoRightRef.current) mencionesLogoRightRef.current.value = '';
                                                            }}
                                                        >
                                                            Cancelar Edición
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Listado */}
                                        <div className="col-md-6">
                                            <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3 h-100" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    📢 Menciones Registradas ({mencionesConfig.menciones.length})
                                                </h3>

                                                <div className="d-flex flex-column gap-2 overflow-auto" style={{ maxHeight: '380px' }}>
                                                    {mencionesConfig.menciones.length === 0 ? (
                                                        <span className="text-muted text-xs text-center py-5">No hay menciones registradas.</span>
                                                    ) : (
                                                        mencionesConfig.menciones.map((m) => (
                                                            <div key={m.id} className="d-flex align-items-center justify-content-between p-2 rounded-3 bg-dark border border-secondary">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    {m.logoDataUrl ? (
                                                                        <img src={m.logoDataUrl} alt="Logo" style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'contain', background: '#fff' }} />
                                                                    ) : (
                                                                        <div className="d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#9333ea', color: '#fff', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                                            {m.nombreInterno.substring(0,2).toUpperCase()}
                                                                        </div>
                                                                    )}
                                                                    <div className="d-flex flex-column">
                                                                        <span className="text-white text-xs fw-bold">{m.nombreInterno}</span>
                                                                        <span className="text-muted text-xxs">{m.formato}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <button 
                                                                        type="button"
                                                                        className="btn btn-link p-0 text-white" 
                                                                        onClick={() => handleMencionEdit(m)}
                                                                        title="Editar"
                                                                    >
                                                                        <i className="bi bi-pencil-square fs-5 text-warning"></i>
                                                                    </button>
                                                                    <button 
                                                                        type="button"
                                                                        className="btn btn-link p-0 text-white" 
                                                                        onClick={() => handleMencionDelete(m.id)}
                                                                        title="Eliminar"
                                                                    >
                                                                        <i className="bi bi-trash-fill fs-5 text-danger"></i>
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Estilos y Posicionamiento */}
                                <div className="col-lg-5 col-md-12">
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3 h-100" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            🎨 Estilos y Ubicación
                                        </h3>

                                        <div className="row g-3">
                                            {/* Colores */}
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Fondo:</label>
                                                <input 
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={mencionesConfig.bgColor}
                                                    onChange={(e) => setMencionesConfig(prev => ({ ...prev, bgColor: e.target.value }))}
                                                />
                                            </div>
                                            <div className="col-6">
                                                <label className="text-muted text-xs">Color Texto:</label>
                                                <input 
                                                    type="color"
                                                    className="w-100 border-0 rounded-2 p-1"
                                                    style={{ height: '32px', cursor: 'pointer', background: 'rgba(0,0,0,0.3)' }}
                                                    value={mencionesConfig.textColor}
                                                    onChange={(e) => setMencionesConfig(prev => ({ ...prev, textColor: e.target.value }))}
                                                />
                                            </div>

                                            {/* <div className="col-6">
                                                <label className="text-muted text-xs">Fuente:</label>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary rounded-2 text-xs"
                                                    value={mencionesConfig.fontFamily}
                                                    onChange={(e) => setMencionesConfig(prev => ({ ...prev, fontFamily: e.target.value }))}
                                                >
                                                    <option value="Montserrat">Montserrat</option>
                                                    <option value="Inter">Inter</option>
                                                    <option value="Oswald">Oswald</option>
                                                    <option value="Roboto">Roboto</option>
                                                </select>
                                            </div> */}

                                            <div className="col-6">
                                                <label className="text-muted text-xs">Ubicación Predefinida:</label>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary rounded-2 text-xs"
                                                    value={mencionesConfig.position}
                                                    onChange={(e) => {
                                                        const pos = e.target.value;
                                                        let x = 50;
                                                        let y = 85;
                                                        if (pos === 'top') { x = 50; y = 15; }
                                                        else if (pos === 'bottom') { x = 50; y = 85; }
                                                        else if (pos === 'center') { x = 50; y = 50; }
                                                        setMencionesConfig(prev => ({ ...prev, position: pos, offsetX: x, offsetY: y }));
                                                    }}
                                                >
                                                    <option value="bottom">Inferior Centrado</option>
                                                    <option value="center">Centro</option>
                                                    <option value="top">Superior Centrado</option>
                                                    <option value="custom">Manual (Personalizado)</option>
                                                </select>
                                            </div>

                                            {mencionesConfig.position === 'custom' && (
                                                <div className="col-12 p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    {renderSliderWithInput("Coordenada X (Manual):", mencionesConfig.offsetX ?? 50, 0, 100, 1, "%", (val) => setMencionesConfig(prev => ({ ...prev, offsetX: val })))}
                                                    {renderSliderWithInput("Coordenada Y (Manual):", mencionesConfig.offsetY ?? 85, 0, 100, 1, "%", (val) => setMencionesConfig(prev => ({ ...prev, offsetY: val })))}
                                                </div>
                                            )}

                                            <div className="col-12 tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Diseño del Banner:</label>
                                                <div className="btn-group w-100" role="group">
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm px-3 fw-bold rounded-start-pill ${!mencionesConfig.isVertical ? 'btn-danger text-white' : 'btn-outline-secondary text-muted'}`}
                                                        onClick={() => setMencionesConfig(prev => ({ ...prev, isVertical: false }))}
                                                    >
                                                        Horizontal
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className={`btn btn-sm px-3 fw-bold rounded-end-pill ${mencionesConfig.isVertical ? 'btn-danger text-white' : 'btn-outline-secondary text-muted'}`}
                                                        onClick={() => setMencionesConfig(prev => ({ ...prev, isVertical: true }))}
                                                    >
                                                        Vertical
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="col-12">
                                                 {renderSliderWithInput("Separación de Elementos:", mencionesConfig.elementSpacing ?? 24, 0, 150, 1, "px", (val) => setMencionesConfig(prev => ({ ...prev, elementSpacing: val })))}
                                             </div>
                                             <div className="col-12">
                                                 {renderSliderWithInput("Zoom de Logotipo/s:", mencionesConfig.logoScale ?? 1.0, 0.2, 6.0, 0.05, "x", (val) => setMencionesConfig(prev => ({ ...prev, logoScale: val })))}
                                             </div>

                                             <div className="col-12">
                                                 {renderSliderWithInput("Zoom General Banner:", mencionesConfig.scale ?? 1.0, 0.4, 6.0, 0.05, "x", (val) => setMencionesConfig(prev => ({ ...prev, scale: val })))}
                                             </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save w-100 mt-auto"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            onClick={handleMencionesSaveGeneral}
                                        >
                                            ⚡ Guardar y Aplicar Ajustes Generales
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA: CONFIGURACIÓN DE PREGUNTAS
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { 
                            setIsPreguntasExpanded(!isPreguntasExpanded); 
                            setIsExpanded(false); 
                            setIsContactExpanded(false); 
                            setIsCoverExpanded(false); 
                            setIsSoporteExpanded(false); 
                            setIsCumpleanosExpanded(false);
                            setIsMencionesExpanded(false);
                        }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-question-circle-fill me-1"></i>
                            <span>Pantalla de preguntas</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: preguntasConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: preguntasConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${preguntasConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {preguntasConfig.visible ? '🟢 Las preguntas se están mostrando al aire' : '🔴 Las preguntas están ocultas'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    preguntasConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: preguntasConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    setIsLoading(true);
                                    const nextVis = !preguntasConfig.visible;
                                    const res = await TogglePreguntasVisibility(nextVis);
                                    setIsLoading(false);
                                    if (res.success) {
                                        setPreguntasConfig(prev => ({ ...prev, visible: nextVis }));
                                        showMessage({ 
                                            type: nextVis ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                                            text: nextVis ? 'Preguntas visibles en pantalla.' : 'Preguntas ocultas de pantalla.' 
                                        });
                                    } else {
                                        showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de preguntas.' });
                                    }
                                }}
                            >
                                {preguntasConfig.visible ? 'Ocultar preguntas' : 'Mostrar preguntas'}
                            </button>
                            <i className={`bi fs-5 ${isPreguntasExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isPreguntasExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Estatus: <strong className="text-white">{preguntasConfig.visible ? 'Visible' : 'Oculto'}</strong> | 
                                Registradas: <strong className="text-white">{preguntasConfig.preguntas.length} preguntas</strong> | 
                                Activa: <strong className="text-white">"{preguntasConfig.activePregunta?.texto || 'Ninguna'}"</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { 
                                    setIsPreguntasExpanded(true); 
                                    setIsExpanded(false); 
                                    setIsContactExpanded(false); 
                                    setIsCoverExpanded(false); 
                                    setIsSoporteExpanded(false); 
                                    setIsCumpleanosExpanded(false);
                                    setIsMencionesExpanded(false);
                                }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {isPreguntasExpanded && (
                        <div className="card-body p-4" style={{ background: '#111522' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Formulario Creador/Editor y Listado de Preguntas (Sección 1) */}
                                <div className="col-lg-7 col-md-12 d-flex flex-column gap-3">
                                    {/* Formulario */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    {editingPreguntaId !== -1 ? '📝 Editar Pregunta' : '✨ Nueva Pregunta'}
                                                </h3>

                                                <div className="tombola-form-group d-flex flex-column gap-2" style={{ fontSize: '0.8rem' }}>
                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Texto de la Pregunta:</label>
                                                        <textarea 
                                                            className="form-control bg-dark text-white border-secondary rounded-2"
                                                            placeholder="Escribe la pregunta aquí..."
                                                            rows="3"
                                                            value={newPreguntaText}
                                                            onChange={(e) => setNewPreguntaText(e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="tombola-input-item d-flex flex-column gap-1">
                                                        <label className="text-muted text-xs fw-semibold">Imagen/Logo Superior (Opcional):</label>
                                                        <input 
                                                            type="file"
                                                            ref={preguntasLogoRef}
                                                            accept="image/*"
                                                            className="d-none"
                                                            onChange={handlePreguntaLogoUpload}
                                                        />
                                                        <div className="d-flex gap-1 align-items-center">
                                                            <button 
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                                onClick={() => preguntasLogoRef.current?.click()}
                                                            >
                                                                Subir Imagen
                                                            </button>
                                                            {preguntasConfig.logoDataUrl && (
                                                                <button 
                                                                    type="button"
                                                                    className="btn btn-sm btn-danger px-2 border-0"
                                                                    onClick={handlePreguntaLogoDelete}
                                                                >
                                                                    Remover
                                                                </button>
                                                            )}
                                                        </div>
                                                        {preguntasConfig.logoDataUrl && (
                                                            <div className="text-center mt-2 p-1 rounded" style={{ background: '#fff' }}>
                                                                <img src={preguntasConfig.logoDataUrl} alt="Preview Logo Pregunta" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="d-flex gap-2 mt-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm flex-fill text-white fw-bold py-2 rounded-2"
                                                        style={{ background: '#ff003c', border: 'none' }}
                                                        onClick={handlePreguntaAdd}
                                                    >
                                                        {editingPreguntaId !== -1 ? 'Guardar Cambios' : 'Agregar Pregunta'}
                                                    </button>
                                                    {editingPreguntaId !== -1 && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-sm btn-outline-secondary text-muted rounded-2"
                                                            onClick={() => {
                                                                setEditingPreguntaId(-1);
                                                                setNewPreguntaText('');
                                                            }}
                                                        >
                                                            Cancelar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Listado */}
                                            <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3 h-100" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', minHeight: '320px' }}>
                                                <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                                    📋 Listado de Preguntas
                                                </h3>

                                                <div className="d-flex flex-column gap-2" style={{ overflowY: 'auto', maxHeight: '380px' }}>
                                                    {preguntasConfig.preguntas.map((p, index) => (
                                                        <div key={p.id || p.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                            <div className="d-flex flex-column gap-0.5 flex-grow-1" style={{ maxWidth: '60%' }}>
                                                                <span className="text-white text-xs fw-bold text-truncate">{p.texto}</span>
                                                                {p.isActive && (
                                                                    <span className="text-success" style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>🟢 Activa en Pantalla</span>
                                                                )}
                                                            </div>
                                                            <div className="d-flex gap-1">
                                                                {/* Mover Arriba */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary p-1"
                                                                    disabled={index === 0}
                                                                    onClick={() => handlePreguntaMoveUp(index)}
                                                                >
                                                                    <i className="bi bi-arrow-up text-white" style={{ fontSize: '0.75rem' }}></i>
                                                                </button>
                                                                {/* Mover Abajo */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary p-1"
                                                                    disabled={index === preguntasConfig.preguntas.length - 1}
                                                                    onClick={() => handlePreguntaMoveDown(index)}
                                                                >
                                                                    <i className="bi bi-arrow-down text-white" style={{ fontSize: '0.75rem' }}></i>
                                                                </button>
                                                                {/* Editar */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary p-1"
                                                                    onClick={() => handlePreguntaEdit(p)}
                                                                >
                                                                    <i className="bi bi-pencil text-warning" style={{ fontSize: '0.75rem' }}></i>
                                                                </button>
                                                                {/* Eliminar */}
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-xs btn-outline-secondary p-1"
                                                                    onClick={() => handlePreguntaDelete(p.id || p.Id)}
                                                                >
                                                                    <i className="bi bi-trash-fill text-danger" style={{ fontSize: '0.75rem' }}></i>
                                                                </button>
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {preguntasConfig.preguntas.length === 0 && (
                                                        <div className="text-center py-5 text-muted text-xs italic">
                                                            No hay preguntas registradas.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                {/* Columna 2: Configuración de Pantalla */}
                                <div className="col-lg-5 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            ⚙️ Ajustes de Visualización
                                        </h3>

                                        <div className="row g-3">
                                            {/* Posición predefinida */}
                                            <div className="col-12 tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Ubicación del Banner:</label>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary rounded-2 text-xs"
                                                    value={preguntasConfig.position}
                                                    onChange={(e) => {
                                                        const pos = e.target.value;
                                                        let x = 50;
                                                        let y = 85;
                                                        if (pos === 'top') { x = 50; y = 15; }
                                                        else if (pos === 'bottom') { x = 50; y = 85; }
                                                        else if (pos === 'center') { x = 50; y = 50; }
                                                        setPreguntasConfig(prev => ({ ...prev, position: pos, offsetX: x, offsetY: y }));
                                                    }}
                                                >
                                                    <option value="bottom">Inferior Centrado</option>
                                                    <option value="center">Centro</option>
                                                    <option value="top">Superior Centrado</option>
                                                    <option value="custom">Manual (Personalizado)</option>
                                                </select>
                                            </div>

                                            {preguntasConfig.position === 'custom' && (
                                                <div className="col-12 p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    {renderSliderWithInput("Coordenada X (Manual):", preguntasConfig.offsetX ?? 50, 0, 100, 1, "%", (val) => setPreguntasConfig(prev => ({ ...prev, offsetX: val })))}
                                                </div>
                                            )}

                                            <div className="col-12">
                                                 {renderSliderWithInput("Posición Vertical (Subir/Bajar):", preguntasConfig.offsetY ?? 85, 0, 100, 1, "%", (val) => setPreguntasConfig(prev => ({ ...prev, offsetY: val })))}
                                            </div>

                                            <div className="col-12">
                                                 {renderSliderWithInput("Zoom General Banner:", preguntasConfig.scale ?? 1.0, 0.4, 6.0, 0.05, "x", (val) => setPreguntasConfig(prev => ({ ...prev, scale: val })))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save w-100 mt-auto"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            onClick={handlePreguntasSaveGeneral}
                                        >
                                            ⚡ Guardar y Aplicar Ajustes Generales
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

                {/* ==========================================================================
                   TARJETA: CONFIGURACIÓN DE PREMIOS (PANTALLA DE PREMIOS)
                   ========================================================================== */}
                <div className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ background: '#0e111a', border: '1px solid rgba(255,255,255,0.06)' }}>
                    {/* Encabezado colapsable */}
                    <div 
                        className="card-header p-3 cursor-pointer d-flex justify-content-between align-items-center text-white"
                        style={{ background: '#2e384d', borderBottom: '1px solid rgba(255,255,255,0.08)' }}
                        onClick={() => { 
                            setIsPremiosExpanded(!isPremiosExpanded); 
                            setIsExpanded(false); 
                            setIsContactExpanded(false); 
                            setIsCoverExpanded(false); 
                            setIsSoporteExpanded(false); 
                            setIsCumpleanosExpanded(false);
                            setIsMencionesExpanded(false);
                            setIsPreguntasExpanded(false);
                        }}
                    >
                        <h5 className="m-0 fw-bold d-flex align-items-center gap-2" style={{ fontSize: '1.05rem', letterSpacing: '0.5px' }}>
                            <i className="bi bi-gift-fill me-1"></i>
                            <span>Pantalla de Premios</span>
                            <span 
                                className="live-status-sphere" 
                                style={{
                                    display: 'inline-block',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    backgroundColor: premiosConfig.visible ? '#22c55e' : '#ef4444',
                                    boxShadow: premiosConfig.visible ? '0 0 10px #22c55e' : '0 0 10px #ef4444',
                                    transition: 'all 0.3s',
                                    marginLeft: '6px'
                                }}
                            ></span>
                        </h5>

                        <div className="d-flex align-items-center gap-3">
                            <span className={`fw-bold text-sm ${premiosConfig.visible ? 'text-success' : 'text-danger'} d-none d-lg-inline`}>
                                {premiosConfig.visible ? '🟢 Los premios se están mostrando al aire' : '🔴 Los premios están ocultos'}
                            </span>
                            <button 
                                className={`btn fw-bold px-3 py-1.5 text-xs rounded-2 text-white border-0 ${
                                    premiosConfig.visible ? 'btn-danger' : 'btn-success'
                                }`}
                                style={{ 
                                    minWidth: '150px', 
                                    fontSize: '0.8rem',
                                    boxShadow: premiosConfig.visible ? '0 2px 8px rgba(220, 38, 38, 0.2)' : '0 2px 8px rgba(22, 163, 74, 0.2)' 
                                }}
                                onClick={async (e) => {
                                    e.stopPropagation();
                                    setIsLoading(true);
                                    const nextVis = !premiosConfig.visible;
                                    const res = await TogglePremiosVisibility(nextVis);
                                    setIsLoading(false);
                                    if (res.success) {
                                        setPremiosConfig(prev => ({ ...prev, visible: nextVis }));
                                        showMessage({ 
                                            type: nextVis ? MESSAGE_TYPE.SUCCESS : MESSAGE_TYPE.INFO, 
                                            text: nextVis ? 'Premios visibles en pantalla.' : 'Premios ocultas de pantalla.' 
                                        });
                                    } else {
                                        showMessage({ type: MESSAGE_TYPE.ERROR, text: 'Error al cambiar visibilidad de premios.' });
                                    }
                                }}
                            >
                                {premiosConfig.visible ? 'Ocultar premios' : 'Mostrar premios'}
                            </button>
                            <i className={`bi fs-5 ${isPremiosExpanded ? 'bi-chevron-up' : 'bi-chevron-down'}`} style={{ color: '#a0aec0' }}></i>
                        </div>
                    </div>

                    {/* Banner Informativo cuando está Plegado */}
                    {!isPremiosExpanded && (
                        <div className="card-body text-muted py-3 px-4 text-xs d-flex justify-content-between align-items-center" style={{ background: '#111827' }}>
                            <span style={{ fontSize: '0.85rem' }}>
                                Estatus: <strong className="text-white">{premiosConfig.visible ? 'Visible' : 'Oculto'}</strong> | 
                                Registrados: <strong className="text-white">{premiosConfig.premios.length} premios</strong> | 
                                Activo: <strong className="text-white">"{premiosConfig.activePremio?.texto || 'Ninguno'}"</strong>
                            </span>
                            <button 
                                className="btn btn-outline-light btn-xs py-1 px-3 text-xs fw-semibold rounded-2" 
                                style={{ fontSize: '0.75rem' }} 
                                onClick={() => { 
                                    setIsPremiosExpanded(true); 
                                    setIsExpanded(false); 
                                    setIsContactExpanded(false); 
                                    setIsCoverExpanded(false); 
                                    setIsSoporteExpanded(false); 
                                    setIsCumpleanosExpanded(false);
                                    setIsMencionesExpanded(false);
                                    setIsPreguntasExpanded(false);
                                }}
                            >
                                Ver ajustes
                            </button>
                        </div>
                    )}

                    {isPremiosExpanded && (
                        <div className="card-body p-4" style={{ background: '#111522' }}>
                            <div className="row g-4">
                                
                                {/* Columna 1: Formulario Creador/Editor y Listado de Premios */}
                                <div className="col-lg-7 col-md-12 d-flex flex-column gap-3">
                                    {/* Formulario */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            {editingPremioId !== -1 ? '📝 Editar Premio' : '✨ Nuevo Premio'}
                                        </h3>

                                        <div className="tombola-form-group d-flex flex-column gap-2" style={{ fontSize: '0.8rem' }}>
                                            <div className="tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Texto o Pregunta del Premio:</label>
                                                <textarea 
                                                    className="form-control bg-dark text-white border-secondary rounded-2"
                                                    placeholder="Escribe el nombre o pregunta del premio..."
                                                    rows="3"
                                                    value={newPremioText}
                                                    onChange={(e) => setNewPremioText(e.target.value)}
                                                />
                                            </div>

                                            <div className="tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Imagen/Logo del Premio (Opcional):</label>
                                                <input 
                                                    type="file"
                                                    ref={premiosLogoRef}
                                                    accept="image/*"
                                                    className="d-none"
                                                    onChange={handlePremioLogoUpload}
                                                />
                                                <div className="d-flex gap-1 align-items-center">
                                                    <button 
                                                        type="button"
                                                        className="btn btn-sm btn-outline-secondary text-white text-xs rounded-2 flex-fill py-1.5"
                                                        onClick={() => premiosLogoRef.current?.click()}
                                                    >
                                                        Subir Imagen
                                                    </button>
                                                    {premiosConfig.logoDataUrl && (
                                                        <button 
                                                            type="button"
                                                            className="btn btn-sm btn-danger px-2 border-0"
                                                            onClick={handlePremioLogoDelete}
                                                        >
                                                            Remover
                                                        </button>
                                                    )}
                                                </div>
                                                {premiosConfig.logoDataUrl && (
                                                    <div className="text-center mt-2 p-1 rounded" style={{ background: '#fff' }}>
                                                        <img src={premiosConfig.logoDataUrl} alt="Preview Logo Premio" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="d-flex gap-2 mt-2">
                                            <button
                                                type="button"
                                                className="btn btn-sm flex-fill text-white fw-bold py-2 rounded-2"
                                                style={{ background: '#ff003c', border: 'none' }}
                                                onClick={handlePremioAdd}
                                            >
                                                {editingPremioId !== -1 ? 'Guardar Cambios' : 'Agregar Premio'}
                                            </button>
                                            {editingPremioId !== -1 && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-secondary text-muted rounded-2"
                                                    onClick={() => {
                                                        setEditingPremioId(-1);
                                                        setNewPremioText('');
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                            )}
                                        </div>
                                    </div>

                                    {/* Listado */}
                                    <div className="tombola-card p-4 rounded-3 d-flex flex-column gap-3 h-100" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)', minHeight: '320px' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            📋 Listado de Premios
                                        </h3>

                                        <div className="d-flex flex-column gap-2" style={{ overflowY: 'auto', maxHeight: '380px' }}>
                                            {premiosConfig.premios.map((p, index) => (
                                                <div key={p.id || p.Id} className="d-flex align-items-center justify-content-between p-2 rounded-3" style={{ background: 'rgba(0,0,0,0.15)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    <div className="d-flex flex-column gap-0.5 flex-grow-1" style={{ maxWidth: '60%' }}>
                                                        <span className="text-white text-xs fw-bold text-truncate">{p.texto}</span>
                                                        {p.isActive && (
                                                            <span className="text-success" style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>🟢 Activo en Pantalla</span>
                                                        )}
                                                    </div>
                                                    <div className="d-flex gap-1">
                                                        {/* Mover Arriba */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline-secondary p-1"
                                                            disabled={index === 0}
                                                            onClick={() => handlePremioMoveUp(index)}
                                                        >
                                                            <i className="bi bi-arrow-up text-white" style={{ fontSize: '0.75rem' }}></i>
                                                        </button>
                                                        {/* Mover Abajo */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline-secondary p-1"
                                                            disabled={index === premiosConfig.premios.length - 1}
                                                            onClick={() => handlePremioMoveDown(index)}
                                                        >
                                                            <i className="bi bi-arrow-down text-white" style={{ fontSize: '0.75rem' }}></i>
                                                        </button>
                                                        {/* Editar */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline-secondary p-1"
                                                            onClick={() => handlePremioEdit(p)}
                                                        >
                                                            <i className="bi bi-pencil text-warning" style={{ fontSize: '0.75rem' }}></i>
                                                        </button>
                                                        {/* Eliminar */}
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline-secondary p-1"
                                                            onClick={() => handlePremioDelete(p.id || p.Id)}
                                                        >
                                                            <i className="bi bi-trash-fill text-danger" style={{ fontSize: '0.75rem' }}></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                            {premiosConfig.premios.length === 0 && (
                                                <div className="text-center py-5 text-muted text-xs italic">
                                                    No hay premios registrados.
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Columna 2: Configuración de Pantalla */}
                                <div className="col-lg-5 col-md-12">
                                    <div className="tombola-card h-100 p-4 rounded-3 d-flex flex-column gap-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.04)' }}>
                                        <h3 className="fs-6 fw-bold text-uppercase pb-2 m-0" style={{ color: '#94a3b8', borderBottom: '1px solid rgba(255,255,255,0.05)', letterSpacing: '1px' }}>
                                            ⚙️ Ajustes de Visualización
                                        </h3>

                                        <div className="row g-3">
                                            {/* Posición predefinida */}
                                            <div className="col-12 tombola-input-item d-flex flex-column gap-1">
                                                <label className="text-muted text-xs fw-semibold">Ubicación del Banner:</label>
                                                <select
                                                    className="form-select bg-dark text-white border-secondary rounded-2 text-xs"
                                                    value={premiosConfig.position}
                                                    onChange={(e) => {
                                                        const pos = e.target.value;
                                                        let x = 50;
                                                        let y = 85;
                                                        if (pos === 'top') { x = 50; y = 15; }
                                                        else if (pos === 'bottom') { x = 50; y = 85; }
                                                        else if (pos === 'center') { x = 50; y = 50; }
                                                        setPremiosConfig(prev => ({ ...prev, position: pos, offsetX: x, offsetY: y }));
                                                    }}
                                                >
                                                    <option value="bottom">Inferior Centrado</option>
                                                    <option value="center">Centro</option>
                                                    <option value="top">Superior Centrado</option>
                                                    <option value="custom">Manual (Personalizado)</option>
                                                </select>
                                            </div>

                                            {premiosConfig.position === 'custom' && (
                                                <div className="col-12 p-2 rounded" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                                                    {renderSliderWithInput("Coordenada X (Manual):", premiosConfig.offsetX ?? 50, 0, 100, 1, "%", (val) => setPremiosConfig(prev => ({ ...prev, offsetX: val })))}
                                                </div>
                                            )}

                                            <div className="col-12">
                                                 {renderSliderWithInput("Posición Vertical (Subir/Bajar):", premiosConfig.offsetY ?? 85, 0, 100, 1, "%", (val) => setPremiosConfig(prev => ({ ...prev, offsetY: val })))}
                                            </div>

                                            <div className="col-12">
                                                 {renderSliderWithInput("Zoom General Banner:", premiosConfig.scale ?? 1.0, 0.4, 6.0, 0.05, "x", (val) => setPremiosConfig(prev => ({ ...prev, scale: val })))}
                                            </div>
                                        </div>

                                        <button
                                            type="button"
                                            className="btn py-2 px-4 rounded-2 text-white fw-bold text-sm btn-save w-100 mt-auto"
                                            style={{ background: '#ff003c', border: 'none', transition: 'all 0.2s', boxShadow: '0 4px 14px rgba(255, 0, 60, 0.3)' }}
                                            onClick={handlePremiosSaveGeneral}
                                        >
                                            ⚡ Guardar y Aplicar Ajustes Generales
                                        </button>
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}
                </div>

            </div>
        </SecondaryBase>
    );
};

export { Tombola };
